import React, { useState, useEffect, useMemo } from 'react';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';

import BaselineEvolutionReport from '../analytics/BaselineEvolutionReport';
import CreativeToolsView from '../analytics/CreativeToolsView';
import SupervisionView from '../analytics/SupervisionView';
import CustomerReportTemplate from '../analytics/CustomerReportTemplate';
import MeetingImportModal from './MeetingImportModal';
import ManualEntryModal from './ManualEntryModal';
import TransactionalAnalysisExperiment from './TransactionalAnalysisExperiment';
import DramaTriangleExperiment from './DramaTriangleExperiment';
import WheelOfLifeEvolution from '../analytics/WheelOfLifeEvolution';
import VoiceDictationButton from '../common/VoiceDictationButton';
import FormattedNoteText from '../common/FormattedNoteText';
import RichTextEditor from '../common/RichTextEditor';
import CounselorNotesTranscriptModal from './CounselorNotesTranscriptModal';
import PeopleCareModal from './PeopleCareModal';
import { getCompetencyBand, COMPETENCY_LEGEND } from '../../constants/peopleCareConstants';
import { sanitizeText, sanitizeHTML } from '../../utils/sanitize';
import { FirebaseService } from '../../services/firebaseService';
import { AuthService } from '../../services/authService';

const DEFAULT_TECHNIQUES = [
    { id: 'respirazione', label: 'Respirazione', icon: '🌬️' },
    { id: 'carte_creative', label: 'Carte Creative', icon: '🎴' },
    { id: 'frequenze', label: 'Frequenze', icon: '🎵' },
    { id: 'sogno', label: 'Lavoro Onirico', icon: '🌙' },
    { id: 'sedia_vuota', label: 'Sedia Vuota', icon: '🪑' },
    { id: 'baseline', label: 'Test Baseline', icon: '📊' },
    { id: 'body_scan', label: 'Grounding', icon: '🦶' },
    { id: 'scrittura', label: 'Scrittura', icon: '📝' }
];

const ClientDetailView = ({ counselor, client, counselorsList: propCounselorsList = [], onBack }) => {
    const [counselorsList, setCounselorsList] = useState(propCounselorsList);
    const [works, setWorks] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [atExperiments, setAtExperiments] = useState([]);
    const [dtExperiments, setDtExperiments] = useState([]);
    const [somaticExperiments, setSomaticExperiments] = useState([]);
    const [showAtExperiment, setShowAtExperiment] = useState(false);
    const [showDtExperiment, setShowDtExperiment] = useState(false);
    const [activeTab, setActiveTab] = useState(client?.mode === 'manual' ? 'meetings' : 'works'); // 'works' | 'meetings'
    const [showBin, setShowBin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [meetingsLoadError, setMeetingsLoadError] = useState(null);
    const [enrichedClient, setEnrichedClient] = useState(client);
    const [clientGroups, setClientGroups] = useState([]);
    const [sortOrder, setSortOrder] = useState('asc');

    // Reassignment state
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [reassignTargetId, setReassignTargetId] = useState('');
    const [reassigning, setReassigning] = useState(false);

    useEffect(() => {
        if (propCounselorsList && propCounselorsList.length > 0) {
            setCounselorsList(propCounselorsList);
        } else {
            DataManager.getAllCounselors().then(list => {
                if (list && list.length > 0) setCounselorsList(list);
            }).catch(e => console.warn("Failed to fetch counselors in ClientDetailView", e));
        }
    }, [propCounselorsList]);

    const counselorEmail = (counselor?.email || '').toLowerCase().trim();
    const isSuperAdmin = Boolean(counselor?.isAdmin || counselor?.role === 'admin' || counselor?.role === 'superadmin' || counselorEmail === 'nuvola@awarenesstool.it' || counselorEmail === 'marilisa.delve@gmail.com');
    const isOrgAdmin = Boolean(counselor?.isOrgAdmin || counselor?.role === 'org_admin' || counselorEmail === 'patrizia.marforio@gmail.com' || counselorEmail === 'marzia.macca@gmail.com' || counselorEmail === 'trebino.martina@gmail.com' || counselorEmail === 'patrizia.marchi.1969@gmail.com');
    const isSupervisor = isSuperAdmin || isOrgAdmin;
    const counselorUid = (counselor?.id || counselor?.uid || counselor?.firebaseUid || '').toLowerCase();
    const clientCounselorId = (client?.counselorId || '').toLowerCase();
    const clientCounselorEmail = (client?.counselorEmail || '').toLowerCase();

    const isAssignedCounselor = 
        !client.counselorId || 
        !client.counselorEmail ||
        client.counselorId === counselor.id ||
        (counselorUid && clientCounselorId === counselorUid) ||
        clientCounselorId === counselorEmail ||
        clientCounselorEmail === counselorEmail ||
        (Array.isArray(counselor?.aliasIds) && counselor.aliasIds.some(aid => aid && aid.toLowerCase() === clientCounselorId)) ||
        (counselorEmail === 'martina@gmail.com' && (clientCounselorEmail === 'martina@gmail.com' || clientCounselorId === 'martina_vaccarone_counselor' || clientCounselorId === 'martina_tavarone_counselor' || (client?.counselorName || '').toLowerCase().includes('vaccarone') || (client?.counselorName || '').toLowerCase().includes('tavarone'))) ||
        (counselorEmail === 'trebino.martina@gmail.com' && (clientCounselorEmail === 'trebino.martina@gmail.com' || clientCounselorId === 'martina_trebino_counselor' || (client?.counselorName || '').toLowerCase().includes('trebino')));

    // Check if counselor has affiliation with BuenaVista
    const counselorHasBuenaVista = Boolean(
        isSuperAdmin || 
        isOrgAdmin || 
        counselor?.organizationId === 'buenavista' || 
        (Array.isArray(counselor?.allowedOrganizations) && counselor.allowedOrganizations.includes('buenavista')) ||
        (counselor?.organizationName || '').toLowerCase().includes('buenavista')
    );

    // Check if client belongs to BuenaVista (strictly by organization tag, not simply settingType)
    const isBuenaVista = Boolean(
        enrichedClient?.organization === 'buenavista' ||
        (enrichedClient?.organizationName || '').toLowerCase().includes('buenavista') ||
        client?.organization === 'buenavista' ||
        (client?.organizationName || '').toLowerCase().includes('buenavista')
    );

    // Marzia, Martina Trebino, Patrizia Marforio e Patrizia Marchi (oltre che Marilisa) possono vedere tutto di tutti i clienti di BuenaVista
    const isAuthorizedBuenaVistaLeader = (isOrgAdmin || isSuperAdmin) && isBuenaVista;

    // Supervision status
    const isForeignSupervisor = isSupervisor && !isAssignedCounselor && !isSuperAdmin;
    const isForeignSuperAdmin = isForeignSupervisor; // alias for backwards compatibility
    const superAdminAccessStatus = enrichedClient?.superAdminAccessStatus || 'none'; // 'none' | 'pending' | 'approved' | 'rejected'
    
    // 1. Lettura completa: i leader autorizzati di BuenaVista vedono tutto (note, colloqui, diari)
    const isAccessApproved = isAuthorizedBuenaVistaLeader || !isForeignSupervisor || superAdminAccessStatus === 'approved';

    // 2. Modifica anagrafica, contratti e percorso organizzativo:
    // "Possono modificare tutto, tranne che le note e diari che competono solo alla counselor che ha il cliente assegnato."
    const canEditClientDetails = isAssignedCounselor || isAuthorizedBuenaVistaLeader || isSuperAdmin;

    // 3. Modifica note e diari: SOLO la counselor assegnata (o superadmin di sistema)
    const canEditNotesAndDiaries = isAssignedCounselor || isSuperAdmin;

    // isReadOnly generale per anagrafica e gestione scheda
    const isReadOnly = !canEditClientDetails;

    // SuperAdmin Access Request States
    const [isRequestingAccess, setIsRequestingAccess] = useState(false);
    const [requestAccessNote, setRequestAccessNote] = useState('');
    const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);

    // Meeting Form State
    const [showMeetingForm, setShowMeetingForm] = useState(false);
    const [editingMeetingId, setEditingMeetingId] = useState(null);
    const [meetingForm, setMeetingForm] = useState({
        date: new Date().toISOString().split('T')[0],
        notes: '',
        tags: '',
        emoji: '😌',
        meetingType: 'colloquio',
        techniques: '',
        exercises: [],
        counselorFeeling: '',
        contractId: ''
    });
    const [showNotesPreview, setShowNotesPreview] = useState(false);
    // Inline Wheel of Life for checkpoint meetings
    const [checkpointWheel, setCheckpointWheel] = useState({
        Lavoro: 5, Famiglia: 5, Relazioni: 5, Salute: 5,
        Emozioni: 5, Finanze: 5, Crescita: 5, Ambiente: 5
    });
    const [showCheckpointWheel, setShowCheckpointWheel] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [showAlertModal, setShowAlertModal] = useState(false);

    // Multi-Contract State
    const contracts = useMemo(() => DataManager.getClientContracts(enrichedClient), [enrichedClient]);
    const [showContractModal, setShowContractModal] = useState(false);
    const [editingContract, setEditingContract] = useState(null);
    const [contractFilterMeetings, setContractFilterMeetings] = useState('all');
    const [selectedContractForReport, setSelectedContractForReport] = useState(null);

    const [isEditingAnagraphic, setIsEditingAnagraphic] = useState(false);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [sendingClientReset, setSendingClientReset] = useState(false);
    const [showPeopleCareModal, setShowPeopleCareModal] = useState(false);
    const [peopleCareDefaultPhase, setPeopleCareDefaultPhase] = useState('IN');
    const [selectedPeopleCareAssessment, setSelectedPeopleCareAssessment] = useState(null);
    const [peopleCareAssessments, setPeopleCareAssessments] = useState([]);

    const [anagraphicForm, setAnagraphicForm] = useState({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        clientId: client.id || '',
        birthDate: client.birthDate || '',
        profession: client.profession || client.workSituation || '',
        hobbies: client.hobbies || '',
        medicalNotes: client.medicalNotes || client.medicalFactors || '',
        generalNotes: client.generalNotes || client.notes || '',
        nextMeetingDate: client.nextMeetingDate || '',
        testPassword: client.initialPassword || '',
        mode: client.mode || 'autonomous',
        isActive: client.isActive !== false,
        settingType: client.settingType || 'individuale',
        organization: client.organization || (client.settingType === 'organizzativo' && counselorHasBuenaVista ? 'buenavista' : null),
        organizationName: client.organizationName || (client.settingType === 'organizzativo' && counselorHasBuenaVista ? 'BuenaVista Cooperativa Sociale' : null),
        managerName: client.managerName || '',
        competencyScore: client.competencyScore !== undefined ? client.competencyScore : '',
        competenciesText: client.competenciesText || '',
        organizationalNeed: client.organizationalNeed || ''
    });

    // Meeting Import Modal State
    const [showImportModal, setShowImportModal] = useState(false);
    const [showManualEntryModal, setShowManualEntryModal] = useState(false);
    const [showNotesTranscriptModal, setShowNotesTranscriptModal] = useState(false);
    const [showClientReport, setShowClientReport] = useState(false);
    const [customTechniques, setCustomTechniques] = useState(counselor?.customTechniques || []);
    const [showOtherInput, setShowOtherInput] = useState(false);
    const [otherTechnique, setOtherTechnique] = useState('');

    // Keep anagraphicForm in sync with enrichedClient after refresh
    useEffect(() => {
        if (!isEditingAnagraphic && !isEditingProfile) {
            setAnagraphicForm({
                name: enrichedClient.name || '',
                email: enrichedClient.email || '',
                phone: enrichedClient.phone || '',
                clientId: enrichedClient.id || client.id || '',
                birthDate: enrichedClient.birthDate || '',
                profession: enrichedClient.profession || enrichedClient.workSituation || '',
                hobbies: enrichedClient.hobbies || '',
                medicalNotes: enrichedClient.medicalNotes || enrichedClient.medicalFactors || '',
                generalNotes: enrichedClient.generalNotes || enrichedClient.notes || '',
                nextMeetingDate: enrichedClient.nextMeetingDate || '',
                testPassword: enrichedClient.initialPassword || '',
                mode: enrichedClient.mode || 'autonomous',
                isActive: enrichedClient.isActive !== false,
                settingType: enrichedClient.settingType || 'individuale',
                organization: enrichedClient.organization || (enrichedClient.settingType === 'organizzativo' && counselorHasBuenaVista ? 'buenavista' : null),
                organizationName: enrichedClient.organizationName || (enrichedClient.settingType === 'organizzativo' && counselorHasBuenaVista ? 'BuenaVista Cooperativa Sociale' : null),
                managerName: enrichedClient.managerName || '',
                competencyScore: enrichedClient.competencyScore !== undefined ? enrichedClient.competencyScore : '',
                competenciesText: enrichedClient.competenciesText || '',
                organizationalNeed: enrichedClient.organizationalNeed || ''
            });
        }
    }, [
        enrichedClient.name, enrichedClient.email, enrichedClient.phone, enrichedClient.id, client.id, 
        enrichedClient.birthDate, enrichedClient.profession, enrichedClient.hobbies, enrichedClient.medicalNotes,
        enrichedClient.generalNotes, enrichedClient.initialPassword, enrichedClient.settingType,
        enrichedClient.managerName, enrichedClient.competencyScore, enrichedClient.competenciesText,
        enrichedClient.organizationalNeed, enrichedClient.nextMeetingDate, enrichedClient.isActive, enrichedClient.mode
    ]);

    useEffect(() => {
        refreshData();
    }, [client, showBin]);

    useEffect(() => {
        if (enrichedClient.mode === 'manual' && activeTab === 'works') {
            setActiveTab('meetings');
        }
    }, [enrichedClient.mode, activeTab]);

    const refreshData = async () => {
        try {
            setLoading(true);
            const results = await Promise.allSettled([
                DataManager.getClientById(client.id),
                DataManager.getClientWorks(client.id, true),
                DataManager.getClientMeetings(client.id, showBin, client.email, client.name, client),
                DataManager.getLatestT0(client.id),
                DataManager.getAssessmentsByClient(client.id),
                DataManager.getAllGroupReports()
            ]);

            results.forEach((res, idx) => {
                if (res.status === 'rejected') {
                    console.error(`Errore caricamento risorsa [${idx}]:`, res.reason);
                }
            });

            if (results[2].status === 'rejected') {
                setMeetingsLoadError(results[2].reason?.message || 'Errore di autorizzazione nel caricamento degli incontri.');
            } else {
                setMeetingsLoadError(null);
            }

            const latestClient = results[0].status === 'fulfilled' ? results[0].value : null;
            const clientWorks = results[1].status === 'fulfilled' ? results[1].value : [];
            let clientMeetings = results[2].status === 'fulfilled' ? results[2].value : [];

            // If no meetings found by query, check embedded meetings on client object
            if ((!clientMeetings || clientMeetings.length === 0)) {
                const embedded = latestClient?.meetings || client?.meetings;
                if (Array.isArray(embedded) && embedded.length > 0) {
                    clientMeetings = embedded.map((m, idx) => ({
                        ...m,
                        id: m.id || m._id || `embedded_${client.id}_${idx}`
                    }));
                }
            }

            // Auto-correct counselor name if client belongs to martina@gmail.com but was mislabeled
            if (latestClient?.counselorEmail === 'martina@gmail.com' && (!latestClient?.counselorName || latestClient?.counselorName?.toLowerCase().includes('trebino') || latestClient?.counselorName?.toLowerCase().includes('tavarone'))) {
                latestClient.counselorName = 'Martina Vaccarone';
                DataManager.updateClient(client.id, { counselorName: 'Martina Vaccarone' }).catch(() => {});
            }

            const latestT0 = results[3].status === 'fulfilled' ? results[3].value : null;
            const assessments = results[4].status === 'fulfilled' ? results[4].value : [];
            const allGroupReports = results[5].status === 'fulfilled' ? results[5].value : [];

            setWorks(clientWorks || []);
            setMeetings(clientMeetings || []);

            const atList = (assessments || []).filter(a => a.type === 'at_experiment');
            const dtList = (assessments || []).filter(a => a.type === 'drama_triangle');
            const somaticList = (assessments || []).filter(a => a.type === 'somatic_experiment');
            const pcList = (assessments || []).filter(a => a.type === 'people_care_in' || a.type === 'people_care_out');

            setAtExperiments(atList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            setDtExperiments(dtList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            setSomaticExperiments(somaticList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            setPeopleCareAssessments(pcList.sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date)));

            // Calculate active groups for this client
            const clientId = client.id || (latestClient && latestClient.id);
            const clientName = (latestClient?.name || client.name || '').toLowerCase().trim();
            const clientEmail = (latestClient?.email || client.email || '').toLowerCase().trim();

            const matchingGroupNames = [];
            (allGroupReports || []).forEach(r => {
                const inSelected = Array.isArray(r.selectedClientIds) && (r.selectedClientIds.includes(clientId) || (client.id && r.selectedClientIds.includes(client.id)));
                const inExt = r.externalParticipants && clientName && r.externalParticipants.toLowerCase().includes(clientName);
                const inExtEmail = r.externalParticipants && clientEmail && r.externalParticipants.toLowerCase().includes(clientEmail);
                if (inSelected || inExt || inExtEmail) {
                    const isSingle = r.meetingCategory === 'single' || r.isSingleMeeting;
                    const groupTitle = isSingle 
                        ? (r.title?.trim() ? `Incontro: ${r.title.trim()}` : 'Incontro Singolo')
                        : `Gruppo: ${r.title?.trim() || 'Senza Titolo'}`;
                    if (!matchingGroupNames.includes(groupTitle)) {
                        matchingGroupNames.push(groupTitle);
                    }
                }
            });
            setClientGroups(matchingGroupNames);

            setEnrichedClient({
                ...client,
                ...(latestClient || {}),
                baselineAssessment: latestT0 ? [latestT0] : []
            });
        } catch (error) {
            console.error("Error refreshing data:", error);
        } finally {
            setLoading(false);
        }
    };

    const sortedMeetings = useMemo(() => {
        const parseDate = (d) => {
            if (!d) return 0;
            if (typeof d === 'string' && d.includes('/')) {
                const p = d.split('/');
                return new Date(p[2], p[1] - 1, p[0]).getTime();
            }
            return new Date(d).getTime();
        };

        let filtered = [...meetings];
        if (contractFilterMeetings !== 'all') {
            filtered = filtered.filter(m => {
                const cId = m.contractId || m.observations?.contractId;
                if (contractFilterMeetings === 'contract_legacy') {
                    return cId === 'contract_legacy' || !cId;
                }
                return cId === contractFilterMeetings;
            });
        }

        const sorted = filtered.sort((a, b) => {
            const valA = parseDate(a.date);
            const valB = parseDate(b.date);
            return sortOrder === 'asc' ? valA - valB : valB - valA;
        });
        return sorted;
    }, [meetings, sortOrder, contractFilterMeetings]);

    // --- Meeting Handlers ---
    const handleSaveMeeting = async () => {
        try {
            const existingMeeting = editingMeetingId ? meetings.find(m => m.id === editingMeetingId) : null;
            const finalNotes = canEditNotesAndDiaries 
                ? meetingForm.notes 
                : (existingMeeting?.counselorNotes || existingMeeting?.notes || '');
            const finalFeeling = canEditNotesAndDiaries 
                ? meetingForm.counselorFeeling 
                : (existingMeeting?.counselorFeeling || '');

            // Require at least notes OR tags (flexible)
            if (!finalNotes && !meetingForm.tags) {
                alert("Inserisci almeno le note di osservazione o i tag della sessione.");
                return;
            }

            const meetingPayload = {
                meetingType: meetingForm.meetingType || 'colloquio',
                techniques: meetingForm.techniques || '',
                exercises: meetingForm.exercises || [],
                contractId: meetingForm.contractId || null
            };

            if (editingMeetingId) {
                // Update existing
                await DataManager.updateMeeting(
                    editingMeetingId, 
                    meetingForm.date, 
                    finalNotes, 
                    meetingForm.tags, 
                    meetingPayload, 
                    meetingForm.emoji, 
                    finalFeeling,
                    meetingForm.contractId || null
                );
            } else {
                // Create new
                await DataManager.createMeeting(
                    client.id, 
                    meetingForm.date, 
                    finalNotes, 
                    meetingForm.emoji, 
                    meetingForm.tags, 
                    meetingPayload, 
                    finalFeeling,
                    meetingForm.contractId || null
                );
            }

            // If checkpoint with wheel, save a baseline snapshot
            if (showCheckpointWheel && (meetingForm.meetingType === 'checkpoint' || meetingForm.meetingType === 'primo_colloquio' || meetingForm.meetingType === 'chiusura_percorso')) {
                await DataManager.saveBaselineAssessment(
                    client.id,
                    meetingForm.meetingType === 'primo_colloquio' ? 'T0' : meetingForm.meetingType === 'chiusura_percorso' ? 'T2' : 'checkpoint',
                    null, // no anamnesis data
                    null, // no qualitative
                    checkpointWheel,
                    meetingForm.contractId || null,
                    new Date(meetingForm.date).toISOString()
                );
            }

            // Auto-analyze keywords after saving
            try {
                await DataManager.analyzeAndUpdateKeywords(client.id);
            } catch (kwErr) {
                console.warn("Could not analyze keywords:", kwErr);
            }

            await refreshData();
            resetMeetingForm();
        } catch (error) {
            console.error("Errore nel salvataggio incontro:", error);
            alert("Errore nel salvataggio: " + error.message);
        }
    };

    const handleAddCustomTechnique = async () => {
        if (!otherTechnique.trim()) return;
        const newTech = otherTechnique.trim();
        
        if (!customTechniques.includes(newTech)) {
            const updated = [...customTechniques, newTech];
            setCustomTechniques(updated);
            
            // Save to counselor profile in Firestore
            try {
                await FirebaseService.updateCounselor(counselor.id, { customTechniques: updated });
            } catch (err) {
                console.error("Error saving custom technique:", err);
            }
        }
        
        // Add to current meeting form
        const currentEx = meetingForm.exercises || [];
        if (!currentEx.includes(newTech)) {
            setMeetingForm({ ...meetingForm, exercises: [...currentEx, newTech] });
        }
        
        setOtherTechnique('');
        setShowOtherInput(false);
    };

    const resetMeetingForm = () => {
        setShowMeetingForm(false);
        setEditingMeetingId(null);
        setMeetingForm({
            date: new Date().toISOString().split('T')[0],
            notes: '',
            tags: '',
            emoji: '😌',
            meetingType: 'colloquio',
            techniques: '',
            exercises: [],
            counselorFeeling: '',
            contractId: ''
        });
        setCheckpointWheel({
            Lavoro: 5, Famiglia: 5, Relazioni: 5, Salute: 5,
            Emozioni: 5, Finanze: 5, Crescita: 5, Ambiente: 5
        });
        setShowCheckpointWheel(false);
        setAlerts([]);
        setShowAlertModal(false);
        setShowNotesPreview(false);
    };

    const handleEditMeeting = (meeting) => {
        setMeetingForm({
            date: meeting.date ? meeting.date.split('T')[0] : new Date().toISOString().split('T')[0],
            notes: meeting.counselorNotes || meeting.notes || '',
            tags: meeting.counselorTags || meeting.tags || '',
            emoji: meeting.counselorEmoji || '😌',
            meetingType: meeting.observations?.meetingType || meeting.meetingType || 'colloquio',
            techniques: meeting.observations?.techniques || '',
            exercises: meeting.observations?.exercises || [],
            counselorFeeling: meeting.counselorFeeling || '',
            contractId: meeting.contractId || meeting.observations?.contractId || ''
        });
        setEditingMeetingId(meeting.id);
        setShowMeetingForm(true);
    };

    const handleDeleteMeeting = async (id) => {
        if (!canEditNotesAndDiaries) {
            alert("⚠️ L'eliminazione degli incontri e delle relative note compete esclusivamente alla counselor assegnata.");
            return;
        }
        if (confirm("Spostare questo incontro nel cestino?")) {
            await DataManager.softDeleteMeeting(id);
            await refreshData();
        }
    };

    const handleRestoreMeeting = async (id) => {
        await DataManager.restoreMeeting(id);
        await refreshData();
    };

    // --- Multi-Contract Handlers ---
    const handleOpenNewContract = () => {
        setEditingContract({
            id: '',
            title: '',
            description: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            status: 'attivo'
        });
        setShowContractModal(true);
    };

    const handleOpenEditContract = (contract) => {
        setEditingContract({
            id: contract.id || '',
            title: contract.title || '',
            description: contract.description || '',
            startDate: contract.startDate || '',
            endDate: contract.endDate || '',
            status: contract.status || 'attivo'
        });
        setShowContractModal(true);
    };

    const handleSaveContractModal = async (contractData) => {
        if (!contractData.title?.trim()) {
            alert("Inserisci un titolo per il contratto di counseling.");
            return;
        }

        try {
            setLoading(true);
            const sanitized = {
                ...contractData,
                title: sanitizeText(contractData.title, 200),
                description: sanitizeHTML(contractData.description || '', ['b', 'i', 'u', 'br', 'p'])
            };
            await DataManager.saveClientContract(client.id, sanitized);
            setShowContractModal(false);
            setEditingContract(null);
            await refreshData();
        } catch (err) {
            console.error("Errore salvataggio contratto:", err);
            alert("Errore nel salvataggio del contratto: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteContract = async (contractId) => {
        if (!confirm("Sei sicuro di voler eliminare questo contratto? Gli incontri associati manterranno il loro storico.")) {
            return;
        }
        try {
            setLoading(true);
            await DataManager.deleteClientContract(client.id, contractId);
            if (contractFilterMeetings === contractId) {
                setContractFilterMeetings('all');
            }
            await refreshData();
        } catch (err) {
            console.error("Errore eliminazione contratto:", err);
            alert("Errore nell'eliminazione del contratto: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleContractStatus = async (contract) => {
        try {
            const newStatus = contract.status === 'attivo' ? 'concluso' : 'attivo';
            await DataManager.saveClientContract(client.id, {
                ...contract,
                status: newStatus,
                endDate: newStatus === 'concluso' ? (contract.endDate || new Date().toISOString().split('T')[0]) : contract.endDate
            });
            await refreshData();
        } catch (err) {
            console.error("Errore modifica stato contratto:", err);
            alert("Errore nella modifica dello stato: " + err.message);
        }
    };

    // --- Anagraphic Handler (Unified Anagrafica) ---
    const handleSaveAnagraphic = async () => {
        // 🔒 SECURITY: Sanitize anagraphic fields
        const sanitizedData = {
            name: sanitizeText(anagraphicForm.name, 100),
            email: sanitizeText(anagraphicForm.email || '', 254),
            phone: sanitizeText(anagraphicForm.phone || '', 50),
            birthDate: anagraphicForm.birthDate || '',
            profession: sanitizeText(anagraphicForm.profession || '', 200),
            workSituation: sanitizeText(anagraphicForm.profession || '', 200),
            hobbies: sanitizeText(anagraphicForm.hobbies || '', 500),
            medicalNotes: canEditNotesAndDiaries 
                ? sanitizeText(anagraphicForm.medicalNotes || '', 1000) 
                : (enrichedClient.medicalNotes || enrichedClient.medicalFactors || ''),
            medicalFactors: canEditNotesAndDiaries 
                ? sanitizeText(anagraphicForm.medicalNotes || '', 1000) 
                : (enrichedClient.medicalNotes || enrichedClient.medicalFactors || ''),
            generalNotes: canEditNotesAndDiaries 
                ? sanitizeText(anagraphicForm.generalNotes || '', 2000) 
                : (enrichedClient.generalNotes || enrichedClient.notes || ''),
            notes: canEditNotesAndDiaries 
                ? sanitizeText(anagraphicForm.generalNotes || '', 2000) 
                : (enrichedClient.generalNotes || enrichedClient.notes || ''),
            nextMeetingDate: anagraphicForm.nextMeetingDate || '',
            mode: anagraphicForm.mode || 'autonomous',
            isActive: anagraphicForm.isActive !== false,
            settingType: anagraphicForm.settingType || 'individuale',
            organization: anagraphicForm.settingType === 'organizzativo' ? (anagraphicForm.organization || (counselorHasBuenaVista ? 'buenavista' : null)) : null,
            organizationName: anagraphicForm.settingType === 'organizzativo' ? (anagraphicForm.organizationName || (counselorHasBuenaVista ? 'BuenaVista Cooperativa Sociale' : null)) : null,
            managerName: anagraphicForm.settingType === 'organizzativo' ? sanitizeText(anagraphicForm.managerName || '', 100) : null,
            competencyScore: (anagraphicForm.settingType === 'organizzativo' && anagraphicForm.competencyScore !== '' && !isNaN(Number(anagraphicForm.competencyScore))) ? Number(anagraphicForm.competencyScore) : null,
            competenciesText: anagraphicForm.settingType === 'organizzativo' ? sanitizeText(anagraphicForm.competenciesText || '', 2000) : null,
            organizationalNeed: anagraphicForm.settingType === 'organizzativo' ? sanitizeText(anagraphicForm.organizationalNeed || '', 2000) : null
        };
        const newClientId = anagraphicForm.clientId?.trim();

        // Validate email format if provided
        if (sanitizedData.email && !sanitizedData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            alert('⚠️ Indirizzo email non valido.');
            return;
        }

        if (!newClientId) {
            alert('⚠️ ID Cliente non può essere vuoto');
            return;
        }

        try {
            setLoading(true);

            if (sanitizedData.email && anagraphicForm.testPassword?.trim()) {
                const pass = anagraphicForm.testPassword.trim();
                if (pass.length < 6) {
                    alert("La password di prova deve contenere almeno 6 caratteri.");
                    setLoading(false);
                    return;
                }
                sanitizedData.initialPassword = pass;
                try {
                    const authRes = await AuthService.createUserWithPassword(sanitizedData.email, pass);
                    if (authRes.created && authRes.uid) {
                        sanitizedData.firebaseUid = authRes.uid;
                    }
                } catch (authErr) {
                    console.info("Auth provision notice:", authErr.message);
                }
            }

            if (newClientId !== client.id) {
                if (!confirm(`Attenzione! Stai per cambiare l'ID del cliente da ${client.id} a ${newClientId}. Questa operazione re-incollerà tutti i dati. Continuare?`)) {
                    setLoading(false);
                    return;
                }
                await DataManager.changeClientId(client.id, newClientId);
            }

            await DataManager.updateClient(newClientId, sanitizedData);
            
            if (newClientId !== client.id) {
                 alert("ID cliente aggiornato con successo. Verrai reindirizzato alla lista clienti.");
                 onBack();
                 return;
            }

            setIsEditingAnagraphic(false);
            setIsEditingProfile(false);
            if (sanitizedData.mode === 'manual' && activeTab === 'works') {
                setActiveTab('meetings');
            }
            await refreshData();
        } catch (e) {
            console.error(e);
            alert("Errore salvataggio: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelProfile = () => {
        setIsEditingProfile(false);
        if (isEditingAnagraphic) {
            setIsEditingAnagraphic(false);
        }
        setAnagraphicForm(prev => ({
            ...prev,
            profession: enrichedClient.profession || enrichedClient.workSituation || '',
            hobbies: enrichedClient.hobbies || '',
            medicalNotes: enrichedClient.medicalNotes || enrichedClient.medicalFactors || '',
            generalNotes: enrichedClient.generalNotes || enrichedClient.notes || ''
        }));
    };

    const handleSaveProfile = async () => {
        try {
            setIsSavingProfile(true);
            const clientId = enrichedClient.id || client.id;
            const med = canEditNotesAndDiaries 
                ? sanitizeText(anagraphicForm.medicalNotes || '', 2000) 
                : (enrichedClient.medicalNotes || enrichedClient.medicalFactors || '');
            const gen = canEditNotesAndDiaries 
                ? sanitizeText(anagraphicForm.generalNotes || '', 3000) 
                : (enrichedClient.generalNotes || enrichedClient.notes || '');

            const updatePayload = {
                profession: prof,
                workSituation: prof,
                hobbies: hob,
                medicalNotes: med,
                medicalFactors: med,
                generalNotes: gen,
                notes: gen
            };

            await DataManager.updateClient(clientId, updatePayload);
            await refreshData();
            setIsEditingProfile(false);
            if (isEditingAnagraphic) {
                setIsEditingAnagraphic(false);
            }
        } catch (error) {
            console.error("Errore salvataggio profilo:", error);
            alert("Errore durante il salvataggio del profilo: " + (error.message || error));
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleSendClientPasswordReset = async () => {
        const email = (enrichedClient.email || client.email || '').trim().toLowerCase();
        if (!email) {
            alert("Nessun indirizzo email configurato per questo cliente.");
            return;
        }
        try {
            setSendingClientReset(true);
            const passToUse = enrichedClient.initialPassword || 'Awareness2026!';
            const res = await AuthService.ensureUserInAuthAndReset(email, passToUse);

            if (res.createdInAuth) {
                // If created in Auth, save UID in client doc
                if (res.uid) {
                    await DataManager.updateClient(client.id, {
                        firebaseUid: res.uid,
                        initialPassword: res.tempPassword
                    });
                    await refreshData();
                }
            }

            let msg = `📧 Email di reimpostazione password inviata a:\n${email}`;
            if (res.createdInAuth) {
                msg += `\n\n🔑 Nota: L'utente non era ancora presente in Firebase Auth ed è stato registrato adesso con password:\n${res.tempPassword}\nL'utente può già accedere direttamente con questa password o usare il link ricevuto via email.`;
            } else {
                msg += `\n\n💡 Se non la trova, controllare la cartella SPAM / Posta Indesiderata.`;
            }
            alert(msg);
        } catch (err) {
            console.error("Error resetting client password:", err);
            alert(`Errore invio reset password: ${err.message}`);
        } finally {
            setSendingClientReset(false);
        }
    };

    // --- Work Handler ---
    const handleDeleteWork = async (id) => {
        if (!canEditNotesAndDiaries) {
            alert("⚠️ La cancellazione delle voci di diario compete esclusivamente alla counselor assegnata.");
            return;
        }
        if (confirm("Eliminare definitivamente questo elemento del diario?")) {
            await DataManager.deleteWork(id);
            await refreshData();
        }
    };

    // --- SuperAdmin / OrgAdmin Access Handlers (Deontological Privacy) ---
    const handleRequestSuperAdminAccess = async () => {
        try {
            setIsRequestingAccess(true);
            const requesterName = counselor.name || (isOrgAdmin ? 'Patrizia Marforio' : (isSuperAdmin ? 'Marilisa Del Vecchio' : counselor.email));
            await DataManager.requestSuperAdminAccess(client.id, {
                requestedBy: counselor.email,
                requestedByName: requesterName,
                role: isOrgAdmin ? 'org_admin' : (isSuperAdmin ? 'admin' : 'counselor'),
                notes: requestAccessNote.trim()
            });
            setShowRequestAccessModal(false);
            setRequestAccessNote('');
            await refreshData();
            alert("✅ Richiesta di autorizzazione inviata con successo al Counselor titolare!");
        } catch (err) {
            console.error("Errore invio richiesta accesso:", err);
            alert("Errore invio richiesta: " + err.message);
        } finally {
            setIsRequestingAccess(false);
        }
    };

    const handleCancelAccessRequest = async () => {
        if (confirm("Annullare la richiesta di accesso agli incontri?")) {
            try {
                setIsRequestingAccess(true);
                await DataManager.cancelSuperAdminAccessRequest(client.id);
                await refreshData();
            } catch (err) {
                console.error("Errore annullamento richiesta:", err);
                alert("Errore annullamento: " + err.message);
            } finally {
                setIsRequestingAccess(false);
            }
        }
    };

    const handleApproveSuperAdminAccess = async () => {
        try {
            setIsRequestingAccess(true);
            await DataManager.approveSuperAdminAccess(client.id, {
                approvedBy: counselor.email,
                approvedByName: counselor.name || counselor.email
            });
            await refreshData();
            alert("✅ Accesso autorizzato! Il SuperAdmin può ora visionare gli incontri in sola lettura per finalità di supervisione.");
        } catch (err) {
            console.error("Errore approvazione accesso:", err);
            alert("Errore approvazione: " + err.message);
        } finally {
            setIsRequestingAccess(false);
        }
    };

    const handleRejectSuperAdminAccess = async () => {
        if (confirm("Rifiutare la richiesta di accesso del SuperAdmin per questo cliente?")) {
            try {
                setIsRequestingAccess(true);
                await DataManager.rejectSuperAdminAccess(client.id, {
                    rejectedBy: counselor.email,
                    rejectedByName: counselor.name || counselor.email
                });
                await refreshData();
            } catch (err) {
                console.error("Errore rifiuto accesso:", err);
                alert("Errore rifiuto: " + err.message);
            } finally {
                setIsRequestingAccess(false);
            }
        }
    };

    const handleRevokeSuperAdminAccess = async () => {
        if (confirm("Revocare l'autorizzazione di visualizzazione al SuperAdmin? Le note degli incontri torneranno private ed esclusive per te.")) {
            try {
                setIsRequestingAccess(true);
                await DataManager.revokeSuperAdminAccess(client.id, {
                    revokedBy: counselor.email,
                    revokedByName: counselor.name || counselor.email
                });
                await refreshData();
                alert("🔒 Accesso revocato con successo.");
            } catch (err) {
                console.error("Errore revoca accesso:", err);
                alert("Errore revoca: " + err.message);
            } finally {
                setIsRequestingAccess(false);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 animate-fade-in">
            <header className="bg-white px-3 py-2 md:p-3 sticky top-0 z-20 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition">←</button>
                    <div className="flex items-center gap-1 md:gap-3">
                        <img src="/logo.png" alt="Logo" className="h-6 md:h-8 object-contain" />
                        <span className="hidden sm:inline text-[10px] md:text-sm font-black tracking-tighter text-slate-800 uppercase leading-none">AwarenessTool</span>
                        <div className="hidden sm:block h-4 w-px bg-slate-200 mx-1"></div>
                        <h1 className="text-xs md:text-sm font-bold text-gray-600 truncate max-w-[120px] md:max-w-none">{client.name}</h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowClientReport(true)}
                        className="px-3.5 py-2 text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition flex items-center gap-1.5"
                        title="Report da consegnare al cliente"
                    >
                        <span>📄</span> <span className="hidden sm:inline">Report</span> Cliente
                    </button>
                </div>
            </header>

            {/* Banner per il Counselor assegnato se c'è una richiesta pendente dal SuperAdmin */}
            {isAssignedCounselor && superAdminAccessStatus === 'pending' && (
                <div className="bg-amber-50/95 border-b-2 border-amber-300 text-slate-900 px-4 py-3.5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-fade-in">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">🔔</span>
                        <div>
                            <p className="text-xs font-black uppercase tracking-wider text-amber-900">Richiesta di Accesso per Supervisione</p>
                            <p className="text-xs font-medium text-slate-800">
                                Il SuperAdmin <strong className="text-slate-900">{enrichedClient.superAdminAccessRequestedByName || enrichedClient.superAdminAccessRequestedBy}</strong> ha richiesto di poter visionare gli incontri di questo cliente a fini di coordinamento/supervisione.
                                {enrichedClient.superAdminAccessNotes && (
                                    <span className="italic block text-slate-700 bg-amber-100/70 border border-amber-200 px-2 py-0.5 rounded text-[11px] mt-1">Nota: «{enrichedClient.superAdminAccessNotes}»</span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                        <button
                            onClick={handleApproveSuperAdminAccess}
                            disabled={isRequestingAccess}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs transition active:scale-95 flex items-center gap-1.5"
                        >
                            <span>✓</span> Approva Accesso
                        </button>
                        <button
                            onClick={handleRejectSuperAdminAccess}
                            disabled={isRequestingAccess}
                            className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition active:scale-95"
                        >
                            ✕ Rifiuta
                        </button>
                    </div>
                </div>
            )}

            {/* Banner per il Counselor assegnato se il SuperAdmin ha accesso approvato */}
            {isAssignedCounselor && superAdminAccessStatus === 'approved' && (
                <div className="bg-emerald-50/90 border-b-2 border-emerald-300 text-emerald-950 px-4 py-3 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-fade-in">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">🛡️</span>
                        <div>
                            <div className="flex items-center gap-2">
                                <p className="text-xs font-black uppercase tracking-wider text-emerald-900">Accesso SuperAdmin Attivo (Supervisione)</p>
                                <span className="bg-emerald-200/80 text-emerald-900 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Concesso
                                </span>
                            </div>
                            <p className="text-xs font-medium text-emerald-800 mt-0.5">
                                Il SuperAdmin (<strong className="text-emerald-950">{enrichedClient.superAdminAccessApprovedByName || enrichedClient.superAdminAccessApprovedBy || 'SuperAdmin'}</strong>) può attualmente visionare gli incontri in sola lettura.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleRevokeSuperAdminAccess}
                        disabled={isRequestingAccess}
                        className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-300 rounded-xl text-xs font-bold transition shadow-xs flex items-center gap-1.5 self-end sm:self-auto flex-shrink-0 active:scale-95"
                        title="Revoca l'autorizzazione di visualizzazione al SuperAdmin"
                    >
                        <span>🔒</span> Rimuovi Accesso SuperAdmin
                    </button>
                </div>
            )}

            {/* Banner per il SuperAdmin in visita su cliente di altro counselor */}
            {isForeignSuperAdmin && (
                <>
                    {superAdminAccessStatus === 'approved' ? (
                        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2.5 text-xs text-emerald-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 font-medium">
                                <span className="text-base">🛡️</span>
                                <span><strong>Modalità Supervisione Autorizzata (Sola Lettura):</strong> Il Counselor titolare (<strong>{enrichedClient.superAdminAccessApprovedByName || enrichedClient.superAdminAccessApprovedBy || 'Titolare'}</strong>) ha approvato l'accesso alle note di sessione.</span>
                            </div>
                            <span className="bg-emerald-200/80 text-emerald-900 text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider whitespace-nowrap">SuperAdmin Autorizzato</span>
                        </div>
                    ) : superAdminAccessStatus === 'pending' ? (
                        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-xs text-amber-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 font-medium">
                                <span className="text-base">⏳</span>
                                <span><strong>Richiesta Inviata al Counselor:</strong> In attesa di autorizzazione da parte del Counselor titolare per consultare gli incontri.</span>
                            </div>
                            <button
                                onClick={handleCancelAccessRequest}
                                disabled={isRequestingAccess}
                                className="text-[11px] font-bold text-amber-800 bg-amber-200/70 hover:bg-amber-200 px-2.5 py-1 rounded-lg transition"
                            >
                                Annulla Richiesta
                            </button>
                        </div>
                    ) : superAdminAccessStatus === 'rejected' ? (
                        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2.5 text-xs text-rose-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 font-medium">
                                <span className="text-base">🚫</span>
                                <span><strong>Accesso Riservato:</strong> La richiesta di consultazione degli incontri è stata respinta dal Counselor titolare nel rispetto della riservatezza deontologica.</span>
                            </div>
                            <button
                                onClick={() => setShowRequestAccessModal(true)}
                                className="text-[11px] font-bold text-rose-800 bg-rose-200/70 hover:bg-rose-200 px-2.5 py-1 rounded-lg transition"
                            >
                                Invia Nuova Richiesta
                            </button>
                        </div>
                    ) : (
                        <div className="bg-slate-100 border-b border-slate-200 px-4 py-2.5 text-xs text-slate-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 font-medium">
                                <span className="text-base">🔒</span>
                                <span><strong>Riservatezza Deontologica:</strong> Cartella assegnata ad altro counselor. Per consultare le note di sessione è richiesta l'approvazione del Counselor titolare.</span>
                            </div>
                            <button
                                onClick={() => setShowRequestAccessModal(true)}
                                className="text-[11px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-3 py-1 rounded-lg transition shadow-xs"
                            >
                                📩 Invia Richiesta di Accesso
                            </button>
                        </div>
                    )}
                </>
            )}

            <main className="p-3 md:p-6 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 pb-10">
                {/* LEFT COL - SNELLO */}
                <div className="space-y-4">
                    <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 space-y-4">
                        {/* Client Identity */}
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#24485d] to-[#152e3c] text-white flex items-center justify-center font-black text-lg shadow-sm shrink-0">
                                    {(enrichedClient.name || client.name || 'C').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <h2 className="text-base font-black text-slate-900 truncate leading-tight">
                                        {enrichedClient.name || client.name || 'Cliente'}
                                    </h2>
                                    <p className="text-xs text-slate-500 truncate mt-0.5">{enrichedClient.email || client.email || 'Nessuna email'}</p>
                                </div>
                            </div>
                            {(isSuperAdmin || !isReadOnly) && (
                                <button
                                    onClick={async () => {
                                        const displayName = enrichedClient.name || client.name || "questo cliente";
                                        if (confirm(`Sei sicuro di voler eliminare DEFINITIVAMENTE la cartella di "${displayName}"? L'azione cancellerà tutti i dati associati.`)) {
                                            try {
                                                setLoading(true);
                                                await DataManager.deleteClient(client.id);
                                                alert(`Cartella cliente eliminata con successo.`);
                                                onBack();
                                            } catch (err) {
                                                alert("Errore durante l'eliminazione: " + err.message);
                                                setLoading(false);
                                            }
                                        }
                                    }}
                                    className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition text-xs shrink-0"
                                    title="Elimina Cartella Cliente"
                                >
                                    🗑️
                                </button>
                            )}
                        </div>

                        {/* Stato Cliente (Attivo / Inattivo) */}
                        <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between gap-2">
                            <div>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Stato Cliente</p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className={`w-2.5 h-2.5 rounded-full ${enrichedClient.isActive !== false ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                                    <span className="text-xs font-bold text-slate-800">
                                        {enrichedClient.isActive !== false ? 'Cliente Attivo' : 'Cliente Inattivo'}
                                    </span>
                                </div>
                            </div>
                            {!isReadOnly && (
                                <button
                                    onClick={async () => {
                                        const nextActive = enrichedClient.isActive === false ? true : false;
                                        try {
                                            setLoading(true);
                                            await DataManager.updateClient(client.id, { isActive: nextActive });
                                            await refreshData();
                                        } catch (e) {
                                            alert("Errore aggiornamento stato: " + e.message);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    className={`px-2.5 py-1 rounded-xl text-[11px] font-bold transition shadow-xs flex items-center gap-1 active:scale-95 ${
                                        enrichedClient.isActive === false
                                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                            : 'bg-white hover:bg-slate-200 text-slate-700 border border-slate-200'
                                    }`}
                                >
                                    <span>{enrichedClient.isActive === false ? '⚡ Rendi Attivo' : 'Disattiva'}</span>
                                </button>
                            )}
                        </div>

                        {/* Accesso alla Piattaforma (Con Accesso App / Senza Accesso) */}
                        <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1">
                                    <span>📱</span> Accesso Piattaforma
                                </p>
                                {!isReadOnly && (
                                    <button
                                        onClick={async () => {
                                            const isManual = enrichedClient.mode === 'manual';
                                            const targetMode = isManual ? 'autonomous' : 'manual';
                                            const confirmMsg = isManual
                                                ? 'Abilitare l\'accesso alla piattaforma per questo cliente? Il cliente potrà accedere e la sezione Diario verrà resa disponibile.'
                                                : 'Disabilitare l\'accesso alla piattaforma per questo cliente? Il cliente non potrà accedere e la sezione Diario verrà nascosta (gestione solo counselor).';
                                            if (confirm(confirmMsg)) {
                                                try {
                                                    setLoading(true);
                                                    await DataManager.updateClientMode(client.id, targetMode, enrichedClient.isActive !== false);
                                                    if (targetMode === 'manual' && activeTab === 'works') {
                                                        setActiveTab('meetings');
                                                    }
                                                    await refreshData();
                                                } catch (err) {
                                                    alert('Errore aggiornamento modalità: ' + err.message);
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }
                                        }}
                                        className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-0.5 rounded-lg transition cursor-pointer"
                                    >
                                        {enrichedClient.mode === 'manual' ? '🟢 Abilita Accesso' : '🔒 Disabilita'}
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className={`w-2.5 h-2.5 rounded-full ${enrichedClient.mode === 'manual' ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
                                <span className="text-xs font-bold text-slate-800">
                                    {enrichedClient.mode === 'manual' ? 'Senza Accesso (Manuale)' : 'Con Accesso App (Autonomo)'}
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-tight">
                                {enrichedClient.mode === 'manual'
                                    ? 'Il cliente non accede all\'app. Diario nascosto.'
                                    : 'Il cliente può accedere all\'app e compilare il diario.'}
                            </p>
                        </div>

                        {/* Prossimo Incontro */}
                        <div className="p-3 bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100/80 rounded-2xl shadow-xs">
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest leading-none mb-1">🗓️ Prossimo Incontro</p>
                            <p className="text-sm font-black text-indigo-700 leading-tight">
                                {enrichedClient.nextMeetingDate 
                                    ? new Date(enrichedClient.nextMeetingDate).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                                    : 'Non programmato'}
                            </p>
                        </div>

                        {/* Percorsi Attivi */}
                        <div className="pt-2 border-t border-slate-100">
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <span>🧭</span> Percorsi attivi:
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {(!enrichedClient.settingType || enrichedClient.settingType === 'individuale') && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#f0f5f8] text-[#24485d] border border-[#d1dde5] shadow-2xs">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                        Individuale
                                    </span>
                                )}
                                {enrichedClient.settingType === 'gruppo' && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-900 border border-purple-200 shadow-2xs">
                                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                        Di Gruppo
                                    </span>
                                )}
                                {enrichedClient.settingType === 'organizzativo' && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-teal-50 text-teal-900 border border-teal-200 shadow-2xs">
                                        <span className="w-2 h-2 rounded-full bg-teal-600"></span>
                                        {isBuenaVista ? 'Organizzativo (BuenaVista)' : (enrichedClient.organizationName ? `Organizzativo (${enrichedClient.organizationName})` : 'Organizzativo')}
                                    </span>
                                )}
                                {clientGroups && clientGroups.length > 0 && clientGroups.map((grpLabel, idx) => (
                                    <span 
                                        key={idx} 
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#f0f5f8] text-[#24485d] border border-[#d1dde5] shadow-2xs"
                                        title={`Partecipazione attiva: ${grpLabel}`}
                                    >
                                        <span className={`w-2 h-2 rounded-full ${grpLabel.startsWith('Incontro') ? 'bg-amber-500' : 'bg-[#335d73]'}`}></span>
                                        {grpLabel}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Counselor Assegnato */}
                        <div className="p-3 bg-slate-50 border border-slate-200/70 rounded-2xl">
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] text-indigo-600 font-black uppercase tracking-wider flex items-center gap-1">
                                    <span>🧑‍💼</span> Counselor Assegnato
                                </p>
                                {Boolean(isSuperAdmin || (isOrgAdmin && isBuenaVista)) && (
                                    <button
                                        onClick={() => setShowReassignModal(!showReassignModal)}
                                        className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded-lg transition cursor-pointer"
                                        title="Riassegna questo cliente"
                                    >
                                        <span>🔄</span> {showReassignModal ? 'Chiudi' : 'Riassegna'}
                                    </button>
                                )}
                            </div>
                            <p className="font-bold text-slate-800 text-xs mt-1">
                                {(() => {
                                    const rawEmail = (enrichedClient.counselorEmail || client.counselorEmail || '').toLowerCase().trim();
                                    const rawId = (enrichedClient.counselorId || client.counselorId || '').toLowerCase().trim();
                                    if (rawEmail === 'martina@gmail.com' || rawId === 'martina_vaccarone_counselor' || rawId === 'martina_tavarone_counselor') return 'Martina Vaccarone';
                                    if (rawEmail === 'trebino.martina@gmail.com' || rawId === 'martina_trebino_counselor') return 'Martina Trebino';

                                    const cObj = (counselorsList || []).find(c => 
                                        c.id === (enrichedClient.counselorId || client.counselorId) || 
                                        (c.email && (enrichedClient.counselorEmail || client.counselorEmail) && c.email.toLowerCase() === (enrichedClient.counselorEmail || client.counselorEmail).toLowerCase()) ||
                                        (c.email && (enrichedClient.counselorId || client.counselorId) && c.email.toLowerCase() === (enrichedClient.counselorId || client.counselorId).toLowerCase())
                                    );
                                    if (cObj?.email?.toLowerCase() === 'martina@gmail.com') return 'Martina Vaccarone';
                                    if (cObj?.email?.toLowerCase() === 'trebino.martina@gmail.com') return 'Martina Trebino';
                                    if (rawEmail === 'martina@gmail.com') return 'Martina Vaccarone';
                                    return cObj?.name || enrichedClient.counselorName || client.counselorName || enrichedClient.counselorEmail || client.counselorEmail || (enrichedClient.counselorId || client.counselorId || 'Non assegnato');
                                })()}
                            </p>
                            {/* Reassignment Dropdown for Supervisor */}
                            {showReassignModal && Boolean(isSuperAdmin || (isOrgAdmin && isBuenaVista)) && (
                                <div className="mt-2 pt-2 border-t border-indigo-100 space-y-2">
                                    <select
                                        value={reassignTargetId}
                                        onChange={(e) => setReassignTargetId(e.target.value)}
                                        className="w-full p-1.5 text-xs border border-indigo-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-indigo-400 font-medium text-slate-800"
                                    >
                                        <option value="">-- Seleziona Counselor --</option>
                                        {(isSuperAdmin ? counselorsList : counselorsList.filter(c => c.canOrganizational !== false)).map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} ({c.email})
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={async () => {
                                            if (!reassignTargetId) return;
                                            const targetC = counselorsList.find(c => c.id === reassignTargetId);
                                            if (!targetC) return;
                                            try {
                                                setReassigning(true);
                                                await DataManager.reassignClientCounselor(
                                                    client.id,
                                                    targetC.id,
                                                    targetC.email,
                                                    targetC.name
                                                );
                                                setShowReassignModal(false);
                                                await refreshData();
                                                alert(`✅ Cliente "${enrichedClient.name || client.name}" riassegnato a ${targetC.name || targetC.email}!`);
                                            } catch (err) {
                                                console.error("Errore riassegnazione:", err);
                                                alert("Errore riassegnazione: " + err.message);
                                            } finally {
                                                setReassigning(false);
                                            }
                                        }}
                                        disabled={reassigning || !reassignTargetId}
                                        className="w-full py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition disabled:opacity-50 cursor-pointer shadow-xs"
                                    >
                                        {reassigning ? 'Riassegnazione...' : 'Conferma'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Pulsante rapido per aprire Tab Anagrafica */}
                        <button
                            onClick={() => setActiveTab('anagrafica')}
                            className="w-full py-2.5 px-3 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm active:scale-98"
                        >
                            <span>👤</span> Apri Scheda Anagrafica
                        </button>
                    </div>
                </div>

                {/* RIGHT COL */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Tabs as clickable cards */}
                    <div className="flex gap-1.5 md:gap-2 pb-3 overflow-x-auto no-scrollbar print:hidden -mx-3 px-3 md:mx-0 md:px-0">
                        {[
                            { id: 'anagrafica', label: 'Anagrafica', icon: '👤', show: true },
                            { id: 'works', label: 'Diario', icon: '📔', show: enrichedClient.mode !== 'manual' || (works && works.length > 0) },
                            { id: 'meetings', label: 'Incontri', icon: '🛋️', count: meetings.length, show: true },
                            { id: 'baseline', label: 'Dati & Analisi A.T.', icon: '📊', show: true },
                            { id: 'tools', label: 'Creatività', icon: '🎨', show: true },
                            { id: 'supervision', label: 'Supervisione', icon: '👁️', show: true }
                        ].filter(t => t.show).map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 px-3 py-2 md:px-5 md:py-3 rounded-2xl text-[11px] md:text-xs font-bold transition-all whitespace-nowrap shadow-sm border-2 ${
                                    activeTab === tab.id 
                                        ? tab.color === 'indigo' 
                                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-indigo-100'
                                            : 'bg-slate-900 border-slate-900 text-white shadow-slate-100'
                                        : 'bg-white border-white text-slate-500 hover:border-slate-100 hover:bg-slate-50'
                                }`}
                            >
                                <span className="text-sm md:text-base">{tab.icon}</span>
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                                        activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    <div className="min-h-[300px]">
                        {/* TAB ANAGRAFICA COMPLETA */}
                        {activeTab === 'anagrafica' && (
                            <div className="space-y-6">
                                {/* Dati Anagrafici & Contatti Card */}
                                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                                    <div className="flex justify-between items-center mb-5 pb-3 border-b border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">👤</span>
                                            <div>
                                                <h3 className="font-bold text-base text-gray-800">Scheda Anagrafica & Contatti</h3>
                                                <p className="text-xs text-slate-500">Dati anagrafici primari, credenziali di accesso e recapiti</p>
                                            </div>
                                        </div>
                                        {!isEditingAnagraphic && !isReadOnly && (
                                            <button
                                                onClick={() => setIsEditingAnagraphic(true)}
                                                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition text-xs flex items-center gap-1.5"
                                            >
                                                <span>✏️</span> Modifica Anagrafica
                                            </button>
                                        )}
                                    </div>

                                    {isEditingAnagraphic ? (
                                        <div className="space-y-5 animate-fade-in">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Nome Completo *</label>
                                                    <input
                                                        type="text"
                                                        className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none text-sm"
                                                        value={anagraphicForm.name}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, name: e.target.value })}
                                                        placeholder="Es. Mario Rossi"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">
                                                        Email {anagraphicForm.mode === 'autonomous' ? '(per accesso app)' : '(Opzionale)'}
                                                    </label>
                                                    <input
                                                        type="email"
                                                        autoComplete="off"
                                                        className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none text-sm"
                                                        value={anagraphicForm.email}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, email: e.target.value })}
                                                        placeholder={anagraphicForm.mode === 'autonomous' ? 'mario.rossi@example.com' : 'Opzionale (lascia vuoto se non accede)'}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Telefono / Cellulare</label>
                                                    <input
                                                        type="tel"
                                                        className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none text-sm"
                                                        value={anagraphicForm.phone}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, phone: e.target.value })}
                                                        placeholder="Es. +39 333 1234567"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">ID Cliente (Database)</label>
                                                    <input
                                                        type="text"
                                                        value={anagraphicForm.clientId}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, clientId: e.target.value })}
                                                        className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none font-mono text-xs bg-slate-50 text-gray-700"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 mb-1">Data Di Nascita</label>
                                                    <input
                                                        type="date"
                                                        value={anagraphicForm.birthDate}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, birthDate: e.target.value })}
                                                        className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-indigo-700 mb-1">🗓️ Prossimo Incontro</label>
                                                    <input
                                                        type="datetime-local"
                                                        value={anagraphicForm.nextMeetingDate ? anagraphicForm.nextMeetingDate.substring(0, 16) : ''}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, nextMeetingDate: e.target.value })}
                                                        className="w-full p-3 border-2 border-indigo-200 rounded-xl focus:border-indigo-500 focus:outline-none font-bold bg-indigo-50/50 text-indigo-700 text-sm"
                                                    />
                                                </div>
                                            </div>

                                            {/* Sezione Anamnesi e Profilo Personale in Edit */}
                                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                                                <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                                    <span className="text-base">📋</span>
                                                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-800">
                                                        Profilo Personale & Informazioni Anamnestiche
                                                    </h4>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-700 mb-1">💼 Situazione Lavorativa / Professione</label>
                                                        <input
                                                            type="text"
                                                            value={anagraphicForm.profession}
                                                            onChange={(e) => setAnagraphicForm({ ...anagraphicForm, profession: e.target.value })}
                                                            placeholder="Es. Impiegato, Libero professionista, Inoccupato..."
                                                            className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-700 mb-1">🎨 Hobby & Passioni</label>
                                                        <input
                                                            type="text"
                                                            value={anagraphicForm.hobbies}
                                                            onChange={(e) => setAnagraphicForm({ ...anagraphicForm, hobbies: e.target.value })}
                                                            placeholder="Es. Pittura, Corsa, Fotografia, Lettura..."
                                                            className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="flex justify-between items-center mb-1">
                                                            <label className="block text-xs font-bold text-slate-700">🩺 Fattori Medici / Salute</label>
                                                            {!canEditNotesAndDiaries && <span className="text-[10px] text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">🔒 Riservato counselor titolare</span>}
                                                        </div>
                                                        <textarea
                                                            rows="2"
                                                            disabled={!canEditNotesAndDiaries}
                                                            value={anagraphicForm.medicalNotes}
                                                            onChange={(e) => setAnagraphicForm({ ...anagraphicForm, medicalNotes: e.target.value })}
                                                            placeholder="Eventuali patologie, terapie in corso, aspetti psicofisici rilevanti..."
                                                            className={`w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${!canEditNotesAndDiaries ? 'bg-slate-100 cursor-not-allowed opacity-80' : 'bg-white'}`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="flex justify-between items-center mb-1">
                                                            <label className="block text-xs font-bold text-slate-700">📝 Note Generali & Anamnesi</label>
                                                            {!canEditNotesAndDiaries && <span className="text-[10px] text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">🔒 Riservato counselor titolare</span>}
                                                        </div>
                                                        <textarea
                                                            rows="2"
                                                            disabled={!canEditNotesAndDiaries}
                                                            value={anagraphicForm.generalNotes}
                                                            onChange={(e) => setAnagraphicForm({ ...anagraphicForm, generalNotes: e.target.value })}
                                                            placeholder="Note generali di contesto, famiglia, motivazione iniziale..."
                                                            className={`w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${!canEditNotesAndDiaries ? 'bg-slate-100 cursor-not-allowed opacity-80' : 'bg-white'}`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Accesso alla Piattaforma */}
                                            <div className="pt-3 border-t border-slate-200 space-y-2">
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Accesso alla Piattaforma</label>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <label className={`p-3.5 rounded-2xl border-2 flex items-start gap-2.5 cursor-pointer transition ${
                                                        (!anagraphicForm.mode || anagraphicForm.mode === 'autonomous')
                                                            ? 'bg-indigo-50 border-indigo-500 text-indigo-950 shadow-xs' 
                                                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                    }`}>
                                                        <input
                                                            type="radio"
                                                            name="clientModeRadio"
                                                            checked={!anagraphicForm.mode || anagraphicForm.mode === 'autonomous'}
                                                            onChange={() => setAnagraphicForm({ ...anagraphicForm, mode: 'autonomous' })}
                                                            className="hidden"
                                                        />
                                                        <span className="text-base mt-0.5">🟢</span>
                                                        <div>
                                                            <div className="text-xs font-bold">Con Accesso App (Autonomo)</div>
                                                            <div className="text-[10px] text-slate-500 font-normal">Il cliente accede all'app e compila il diario</div>
                                                        </div>
                                                    </label>
                                                    <label className={`p-3.5 rounded-2xl border-2 flex items-start gap-2.5 cursor-pointer transition ${
                                                        anagraphicForm.mode === 'manual'
                                                            ? 'bg-amber-50 border-amber-500 text-amber-950 shadow-xs' 
                                                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                    }`}>
                                                        <input
                                                            type="radio"
                                                            name="clientModeRadio"
                                                            checked={anagraphicForm.mode === 'manual'}
                                                            onChange={() => setAnagraphicForm({ ...anagraphicForm, mode: 'manual' })}
                                                            className="hidden"
                                                        />
                                                        <span className="text-base mt-0.5">🔒</span>
                                                        <div>
                                                            <div className="text-xs font-bold">Senza Accesso (Solo Counselor)</div>
                                                            <div className="text-[10px] text-slate-500 font-normal">Nessun diario, nessun accesso, email opzionale</div>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            {anagraphicForm.mode !== 'manual' ? (
                                                <div>
                                                    <label className="block text-xs font-bold text-amber-700 mb-1">🔑 Password di Prova / Accesso</label>
                                                    <input
                                                        type="text"
                                                        value={anagraphicForm.testPassword || ''}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, testPassword: e.target.value })}
                                                        placeholder="Es. Benvenuto2026!"
                                                        className="w-full p-3 border-2 border-amber-200 rounded-xl focus:border-amber-500 focus:outline-none font-mono text-sm bg-amber-50/40 text-gray-800"
                                                    />
                                                    <p className="text-[11px] text-gray-500 mt-1">
                                                        Se impostata, crea o allinea l'account in Firebase Auth consentendo all'utente di accedere subito.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="p-3 bg-amber-50/70 rounded-2xl border border-amber-200 text-xs text-amber-800">
                                                    ℹ️ In modalità <strong>Senza Accesso (Manuale)</strong> il cliente non ha credenziali per accedere alla piattaforma e la scheda Diario rimane disabilitata.
                                                </div>
                                            )}

                                            {/* Stato Cliente */}
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Stato Cliente</label>
                                                <div className="flex gap-3">
                                                    <label className={`flex-1 p-3 rounded-2xl border-2 flex items-center justify-center gap-2 cursor-pointer font-bold text-xs transition ${
                                                        anagraphicForm.isActive !== false 
                                                            ? 'bg-emerald-50 border-emerald-500 text-emerald-900 shadow-xs' 
                                                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                    }`}>
                                                        <input
                                                            type="radio"
                                                            name="clientStatusRadio"
                                                            checked={anagraphicForm.isActive !== false}
                                                            onChange={() => setAnagraphicForm({ ...anagraphicForm, isActive: true })}
                                                            className="hidden"
                                                        />
                                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                                                        <span>● Attivo</span>
                                                    </label>
                                                    <label className={`flex-1 p-3 rounded-2xl border-2 flex items-center justify-center gap-2 cursor-pointer font-bold text-xs transition ${
                                                        anagraphicForm.isActive === false 
                                                            ? 'bg-amber-50 border-amber-500 text-amber-900 shadow-xs' 
                                                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                    }`}>
                                                        <input
                                                            type="radio"
                                                            name="clientStatusRadio"
                                                            checked={anagraphicForm.isActive === false}
                                                            onChange={() => setAnagraphicForm({ ...anagraphicForm, isActive: false })}
                                                            className="hidden"
                                                        />
                                                        <span className="w-2.5 h-2.5 rounded-full bg-amber-600"></span>
                                                        <span>○ Inattivo</span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Setting / Tipo Percorso */}
                                            <div className="pt-3 border-t border-slate-200 space-y-2">
                                                 <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Tipo di Percorso</label>
                                                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                     <label className={`p-3.5 rounded-2xl border-2 flex items-center justify-center gap-2 cursor-pointer font-bold text-xs transition ${
                                                         (!anagraphicForm.settingType || anagraphicForm.settingType === 'individuale')
                                                             ? 'bg-sky-50 border-sky-500 text-sky-900 shadow-xs' 
                                                             : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                     }`}>
                                                         <input
                                                             type="radio"
                                                             name="settingTypeRadio"
                                                             checked={!anagraphicForm.settingType || anagraphicForm.settingType === 'individuale'}
                                                             onChange={() => setAnagraphicForm({ ...anagraphicForm, settingType: 'individuale' })}
                                                             className="hidden"
                                                         />
                                                         <span>👤 Individuale</span>
                                                     </label>
                                                     <label className={`p-3.5 rounded-2xl border-2 flex items-center justify-center gap-2 cursor-pointer font-bold text-xs transition ${
                                                         anagraphicForm.settingType === 'gruppo'
                                                             ? 'bg-purple-50 border-purple-500 text-purple-950 shadow-xs' 
                                                             : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                     }`}>
                                                         <input
                                                             type="radio"
                                                             name="settingTypeRadio"
                                                             checked={anagraphicForm.settingType === 'gruppo'}
                                                             onChange={() => setAnagraphicForm({ ...anagraphicForm, settingType: 'gruppo' })}
                                                             className="hidden"
                                                         />
                                                         <span>👥 Di Gruppo</span>
                                                     </label>
                                                     <label className={`p-3.5 rounded-2xl border-2 flex flex-col items-center justify-center gap-0.5 cursor-pointer font-bold text-xs transition ${
                                                         anagraphicForm.settingType === 'organizzativo' 
                                                             ? 'bg-teal-50 border-teal-600 text-teal-950 shadow-xs' 
                                                             : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                                     }`}>
                                                         <input
                                                             type="radio"
                                                             name="settingTypeRadio"
                                                             checked={anagraphicForm.settingType === 'organizzativo'}
                                                             onChange={() => setAnagraphicForm({
                                                                 ...anagraphicForm,
                                                                 settingType: 'organizzativo',
                                                                 organization: counselorHasBuenaVista ? 'buenavista' : (anagraphicForm.organization || null),
                                                                 organizationName: counselorHasBuenaVista ? 'BuenaVista Cooperativa Sociale' : (anagraphicForm.organizationName || null)
                                                             })}
                                                             className="hidden"
                                                         />
                                                         <span className="flex items-center gap-1.5">🏢 Organizzativo</span>
                                                         {counselorHasBuenaVista && <span className="text-[10px] text-teal-700 font-medium">BuenaVista</span>}
                                                     </label>
                                                 </div>
                                             </div>

                                            {/* Campi Organizzativi (Condizionali in Edit) */}
                                            {anagraphicForm.settingType === 'organizzativo' && (
                                                <div className="p-4 bg-teal-50/60 rounded-2xl border border-teal-200 space-y-3 animate-fade-in">
                                                    <div className="flex items-center gap-2 border-b border-teal-200/60 pb-2">
                                                        <span className="text-base">🏢</span>
                                                        <h4 className="text-xs font-black uppercase tracking-wider text-teal-900">
                                                            Dati Percorso Organizzativo{counselorHasBuenaVista || isBuenaVista ? ' — BuenaVista' : ''}
                                                        </h4>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-700 mb-1">Responsabile Gerarchico</label>
                                                        <input
                                                            type="text"
                                                            value={anagraphicForm.managerName}
                                                            onChange={e => setAnagraphicForm({ ...anagraphicForm, managerName: e.target.value })}
                                                            placeholder="es. Federica Ciabattari"
                                                            className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                                        />
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <label className="text-xs font-bold text-slate-700">Indice Competenze Trasversali (1.00 - 5.00)</label>
                                                            {anagraphicForm.competencyScore && (() => {
                                                                const band = getCompetencyBand(anagraphicForm.competencyScore);
                                                                return band ? (
                                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${band.badgeClass}`}>
                                                                        {band.label}
                                                                    </span>
                                                                ) : null;
                                                            })()}
                                                        </div>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="1"
                                                            max="5"
                                                            value={anagraphicForm.competencyScore}
                                                            onChange={e => setAnagraphicForm({ ...anagraphicForm, competencyScore: e.target.value })}
                                                            placeholder="es. 3.04"
                                                            className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-700 mb-1">Competenze Trasversali (Sintesi)</label>
                                                        <textarea
                                                            rows="2"
                                                            value={anagraphicForm.competenciesText}
                                                            onChange={e => setAnagraphicForm({ ...anagraphicForm, competenciesText: e.target.value })}
                                                            placeholder="Punti di forza e aree di consolidamento..."
                                                            className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-700 mb-1">Bisogno Organizzativo</label>
                                                        <textarea
                                                            rows="2"
                                                            value={anagraphicForm.organizationalNeed}
                                                            onChange={e => setAnagraphicForm({ ...anagraphicForm, organizationalNeed: e.target.value })}
                                                            placeholder="es. Rafforzare leadership, comunicazione, delega..."
                                                            className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex gap-2 pt-3">
                                                <Button onClick={handleSaveAnagraphic} className="px-5 py-2.5 text-sm">Salva Modifiche</Button>
                                                <button
                                                    onClick={() => {
                                                        setIsEditingAnagraphic(false);
                                                        setAnagraphicForm({ 
                                                            name: enrichedClient.name || '', 
                                                            email: enrichedClient.email || '', 
                                                            phone: enrichedClient.phone || '',
                                                            clientId: enrichedClient.id || client.id,
                                                            birthDate: enrichedClient.birthDate || '',
                                                            profession: enrichedClient.profession || enrichedClient.workSituation || '',
                                                            hobbies: enrichedClient.hobbies || '',
                                                            medicalNotes: enrichedClient.medicalNotes || enrichedClient.medicalFactors || '',
                                                            generalNotes: enrichedClient.generalNotes || enrichedClient.notes || '',
                                                            nextMeetingDate: enrichedClient.nextMeetingDate || '',
                                                            testPassword: enrichedClient.initialPassword || '',
                                                            mode: enrichedClient.mode || 'autonomous',
                                                            isActive: enrichedClient.isActive !== false,
                                                            settingType: enrichedClient.settingType || 'individuale',
                                                            organization: enrichedClient.organization || (counselorHasBuenaVista ? 'buenavista' : null),
                                                            organizationName: enrichedClient.organizationName || (counselorHasBuenaVista ? 'BuenaVista Cooperativa Sociale' : null),
                                                            managerName: enrichedClient.managerName || '',
                                                            competencyScore: enrichedClient.competencyScore !== undefined ? enrichedClient.competencyScore : '',
                                                            competenciesText: enrichedClient.competenciesText || '',
                                                            organizationalNeed: enrichedClient.organizationalNeed || ''
                                                        });
                                                    }}
                                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition text-sm font-bold"
                                                >
                                                    Annulla
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-5">
                                            {/* Griglia Dati Cliente */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Nome Completo</p>
                                                    <p className="font-bold text-gray-800 text-sm mt-0.5">{enrichedClient.name || '-'}</p>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Email</p>
                                                    <div className="flex items-center justify-between gap-1 mt-0.5">
                                                        <p className="font-bold text-gray-800 text-sm truncate">{enrichedClient.email || '-'}</p>
                                                        <button
                                                            onClick={handleSendClientPasswordReset}
                                                            disabled={sendingClientReset || !enrichedClient.email}
                                                            title="Invia email di reset password"
                                                            className="text-[10px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-0.5 rounded-lg transition shrink-0"
                                                        >
                                                            {sendingClientReset ? 'Invio...' : '📧 Reset Pwd'}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Telefono</p>
                                                    <p className="font-bold text-gray-800 text-sm mt-0.5">{enrichedClient.phone || '-'}</p>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Data di Nascita & Età</p>
                                                    <p className="font-bold text-gray-800 text-sm mt-0.5">
                                                        {enrichedClient.birthDate ? (
                                                            <>
                                                                {(() => {
                                                                    const dob = new Date(enrichedClient.birthDate);
                                                                    const age = Math.abs(new Date(Date.now() - dob.getTime()).getUTCFullYear() - 1970);
                                                                    return isNaN(age) ? '-' : age + ' anni';
                                                                })()}
                                                                <span className="text-xs text-gray-400 ml-1.5 font-normal">({new Date(enrichedClient.birthDate).toLocaleDateString('it-IT')})</span>
                                                            </>
                                                        ) : '-'}
                                                    </p>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Tipo di Percorso</p>
                                                    <div className="mt-1">
                                                        {enrichedClient.settingType === 'organizzativo' ? (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-teal-50 text-teal-900 border border-teal-200">
                                                                <span className="w-2 h-2 rounded-full bg-teal-600"></span>
                                                                {isBuenaVista ? 'Organizzativo — BuenaVista' : (enrichedClient.organizationName ? `Organizzativo — ${enrichedClient.organizationName}` : 'Organizzativo')}
                                                            </span>
                                                        ) : enrichedClient.settingType === 'gruppo' ? (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-900 border border-purple-200">
                                                                <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                                                                Di Gruppo
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#f0f5f8] text-[#24485d] border border-[#d1dde5]">
                                                                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                                                Individuale
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Accesso Piattaforma</p>
                                                    <div className="mt-1">
                                                        {enrichedClient.mode === 'manual' ? (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                                                                <span className="w-2 h-2 rounded-full bg-amber-500"></span> Senza Accesso (Manuale)
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-800 border border-indigo-200">
                                                                <span className="w-2 h-2 rounded-full bg-indigo-500"></span> Con Accesso App
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Stato Cliente</p>
                                                    <div className="mt-1">
                                                        {enrichedClient.isActive !== false ? (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                                                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Attivo
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                                                                <span className="w-2 h-2 rounded-full bg-amber-500"></span> Inattivo
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">ID Univoco Cliente</p>
                                                    <p className="text-xs font-mono text-gray-700 tracking-tight mt-1">{client.id}</p>
                                                </div>
                                                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Password di Prova</p>
                                                    <p className="text-xs font-mono text-amber-800 mt-1">
                                                        {enrichedClient.initialPassword ? (
                                                            <span className="bg-amber-50 px-2 py-0.5 rounded border border-amber-200">{enrichedClient.initialPassword}</span>
                                                        ) : (
                                                            <span className="text-slate-400 italic">Non impostata</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Profilo Personale & Note Anamnestiche (Anagrafica Unica) */}
                                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 space-y-4">
                                    <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">📋</span>
                                            <div>
                                                <h3 className="font-bold text-base text-gray-800">Profilo Personale & Informazioni Anamnestiche</h3>
                                                <p className="text-xs text-slate-500">Situazione lavorativa, passioni, anamnesi medica e note generali consolidate</p>
                                            </div>
                                        </div>
                                        {(isEditingProfile || isEditingAnagraphic) ? (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleCancelProfile}
                                                    disabled={isSavingProfile}
                                                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition text-xs"
                                                >
                                                    Annulla
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveProfile}
                                                    disabled={isSavingProfile}
                                                    className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-xs transition text-xs flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    {isSavingProfile ? (
                                                        <span>Salvataggio...</span>
                                                    ) : (
                                                        <>
                                                            <span>💾</span> Salva
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        ) : !isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => setIsEditingProfile(true)}
                                                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition text-xs flex items-center gap-1"
                                            >
                                                <span>✏️</span> Modifica
                                            </button>
                                        )}
                                    </div>

                                    {(isEditingProfile || isEditingAnagraphic) ? (
                                        <div className="space-y-4 animate-fade-in">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="p-4 bg-slate-50/80 rounded-2xl border-2 border-indigo-100 focus-within:border-indigo-400 focus-within:bg-white transition space-y-1.5">
                                                    <label className="text-[11px] font-bold text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>💼</span> Situazione Lavorativa / Professione
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={anagraphicForm.profession || ''}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, profession: e.target.value })}
                                                        placeholder="Es. Impiegato bancario, Docente, Libero professionista..."
                                                        className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                    />
                                                </div>

                                                <div className="p-4 bg-slate-50/80 rounded-2xl border-2 border-indigo-100 focus-within:border-indigo-400 focus-within:bg-white transition space-y-1.5">
                                                    <label className="text-[11px] font-bold text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>🎨</span> Hobby & Passioni
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={anagraphicForm.hobbies || ''}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, hobbies: e.target.value })}
                                                        placeholder="Es. Pittura, Corsa, Fotografia, Lettura..."
                                                        className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                    />
                                                </div>

                                                <div className="p-4 bg-slate-50/80 rounded-2xl border-2 border-indigo-100 focus-within:border-indigo-400 focus-within:bg-white transition space-y-1.5">
                                                    <label className="text-[11px] font-bold text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>🩺</span> Fattori Medici & Salute
                                                    </label>
                                                    <textarea
                                                        rows={3}
                                                        value={anagraphicForm.medicalNotes || ''}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, medicalNotes: e.target.value })}
                                                        placeholder="Eventuali patologie, terapie in corso, aspetti psicofisici rilevanti..."
                                                        className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                    />
                                                </div>

                                                <div className="p-4 bg-slate-50/80 rounded-2xl border-2 border-indigo-100 focus-within:border-indigo-400 focus-within:bg-white transition space-y-1.5">
                                                    <label className="text-[11px] font-bold text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>📝</span> Note Generali & Anamnesi
                                                    </label>
                                                    <textarea
                                                        rows={3}
                                                        value={anagraphicForm.generalNotes || ''}
                                                        onChange={(e) => setAnagraphicForm({ ...anagraphicForm, generalNotes: e.target.value })}
                                                        placeholder="Note generali di contesto, famiglia, motivazione iniziale..."
                                                        className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex justify-end items-center gap-2 pt-2 border-t border-slate-100">
                                                <button
                                                    type="button"
                                                    onClick={handleCancelProfile}
                                                    disabled={isSavingProfile}
                                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition text-xs"
                                                >
                                                    Annulla
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveProfile}
                                                    disabled={isSavingProfile}
                                                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-xs transition text-xs flex items-center gap-1.5 disabled:opacity-50"
                                                >
                                                    {isSavingProfile ? (
                                                        <span>Salvataggio...</span>
                                                    ) : (
                                                        <>
                                                            <span>💾</span> Salva Modifiche Profilo
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div
                                                onClick={() => !isReadOnly && setIsEditingProfile(true)}
                                                className={`p-4 bg-slate-50/70 rounded-2xl border border-slate-100 space-y-1 ${!isReadOnly ? 'cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/20 transition' : ''}`}
                                                title={!isReadOnly ? "Clicca per modificare" : undefined}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>💼</span> Situazione Lavorativa / Professione
                                                    </p>
                                                    {!isReadOnly && <span className="text-[10px] text-slate-400 opacity-60">✏️</span>}
                                                </div>
                                                <p className="text-xs text-slate-800 font-semibold leading-relaxed pt-1">
                                                    {enrichedClient.profession || enrichedClient.workSituation || (
                                                        <span className="text-slate-400 italic font-normal">Non specificata</span>
                                                    )}
                                                </p>
                                            </div>

                                            <div
                                                onClick={() => !isReadOnly && setIsEditingProfile(true)}
                                                className={`p-4 bg-slate-50/70 rounded-2xl border border-slate-100 space-y-1 ${!isReadOnly ? 'cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/20 transition' : ''}`}
                                                title={!isReadOnly ? "Clicca per modificare" : undefined}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>🎨</span> Hobby & Passioni
                                                    </p>
                                                    {!isReadOnly && <span className="text-[10px] text-slate-400 opacity-60">✏️</span>}
                                                </div>
                                                <p className="text-xs text-slate-800 font-semibold leading-relaxed pt-1">
                                                    {enrichedClient.hobbies || (
                                                        <span className="text-slate-400 italic font-normal">Nessun hobby indicato</span>
                                                    )}
                                                </p>
                                            </div>

                                            <div
                                                onClick={() => !isReadOnly && setIsEditingProfile(true)}
                                                className={`p-4 bg-slate-50/70 rounded-2xl border border-slate-100 space-y-1 ${!isReadOnly ? 'cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/20 transition' : ''}`}
                                                title={!isReadOnly ? "Clicca per modificare" : undefined}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>🩺</span> Fattori Medici & Salute
                                                    </p>
                                                    {!isReadOnly && <span className="text-[10px] text-slate-400 opacity-60">✏️</span>}
                                                </div>
                                                <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed pt-1">
                                                    {enrichedClient.medicalNotes || enrichedClient.medicalFactors || (
                                                        <span className="text-slate-400 italic font-normal">Nessun fattore medico o di salute registrato</span>
                                                    )}
                                                </p>
                                            </div>

                                            <div
                                                onClick={() => !isReadOnly && setIsEditingProfile(true)}
                                                className={`p-4 bg-slate-50/70 rounded-2xl border border-slate-100 space-y-1 ${!isReadOnly ? 'cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/20 transition' : ''}`}
                                                title={!isReadOnly ? "Clicca per modificare" : undefined}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                                        <span>📝</span> Note Generali & Anamnesi
                                                    </p>
                                                    {!isReadOnly && <span className="text-[10px] text-slate-400 opacity-60">✏️</span>}
                                                </div>
                                                <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed pt-1">
                                                    {enrichedClient.generalNotes || enrichedClient.notes || (
                                                        <span className="text-slate-400 italic font-normal">Nessuna nota generale registrata</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Scheda Percorso Organizzativo (Condizionale) */}
                                {enrichedClient.settingType === 'organizzativo' && (
                                    <div className="bg-gradient-to-br from-teal-50/70 to-emerald-50/70 p-6 rounded-3xl border border-teal-200 shadow-sm space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xl">🏢</span>
                                                <div>
                                                    <h3 className="font-bold text-base text-teal-950">
                                                        Dettaglio Percorso Organizzativo{isBuenaVista ? ' — BuenaVista' : (enrichedClient.organizationName ? ` — ${enrichedClient.organizationName}` : '')}
                                                    </h3>
                                                    <p className="text-xs text-teal-700">Competenze trasversali, responsabile e questionari People Care</p>
                                                </div>
                                            </div>
                                            {enrichedClient.managerName && (
                                                <span className="text-xs bg-white/90 text-teal-900 border border-teal-200 font-bold px-3 py-1 rounded-xl shadow-2xs">
                                                    👤 Referente: {enrichedClient.managerName}
                                                </span>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Competenze Trasversali */}
                                            <div className="p-4 bg-white rounded-2xl border border-teal-100 shadow-2xs space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold text-slate-600 uppercase">Indice Competenze Trasversali</span>
                                                    <span className="text-base font-black text-slate-900">
                                                        {enrichedClient.competencyScore !== undefined && enrichedClient.competencyScore !== null && enrichedClient.competencyScore !== '' 
                                                            ? Number(enrichedClient.competencyScore).toFixed(2) 
                                                            : 'Non rilevato'}
                                                    </span>
                                                </div>
                                                {enrichedClient.competencyScore !== undefined && enrichedClient.competencyScore !== null && enrichedClient.competencyScore !== '' && (() => {
                                                    const band = getCompetencyBand(enrichedClient.competencyScore);
                                                    return band ? (
                                                        <div className={`px-2.5 py-1 rounded-lg text-xs font-black border text-center ${band.badgeClass}`}>
                                                            {band.label}
                                                        </div>
                                                    ) : null;
                                                })()}
                                                {enrichedClient.competenciesText && (
                                                    <p className="text-xs text-slate-600 pt-1 border-t border-slate-100">{enrichedClient.competenciesText}</p>
                                                )}
                                            </div>

                                            {/* Bisogno Organizzativo */}
                                            <div className="p-4 bg-white rounded-2xl border border-teal-100 shadow-2xs space-y-1">
                                                <span className="text-xs font-bold text-teal-900 uppercase">Bisogno Organizzativo Rilevato</span>
                                                <p className="text-xs text-slate-700 leading-relaxed">
                                                    {enrichedClient.organizationalNeed || 'Nessun bisogno organizzativo specificato al momento.'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Questionari People Care */}
                                        <div className="pt-3 border-t border-teal-200/60 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-black uppercase tracking-wider text-teal-950">Questionari People Care (Rilevazioni IN / OUT)</span>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedPeopleCareAssessment(null);
                                                            setPeopleCareDefaultPhase('IN');
                                                            setShowPeopleCareModal(true);
                                                        }}
                                                        className="py-1.5 px-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition shadow-xs flex items-center gap-1"
                                                    >
                                                        <span>🌱</span> Nuova Rilevazione IN
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedPeopleCareAssessment(null);
                                                            setPeopleCareDefaultPhase('OUT');
                                                            setShowPeopleCareModal(true);
                                                        }}
                                                        className="py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-xs flex items-center gap-1"
                                                    >
                                                        <span>🌳</span> Nuova Rilevazione OUT
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Existing Questionnaires */}
                                            {peopleCareAssessments.length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                                                    {peopleCareAssessments.map(pc => {
                                                        const isOut = pc.type === 'people_care_out' || pc.phase === 'OUT';
                                                        return (
                                                            <div
                                                                key={pc.id}
                                                                onClick={() => {
                                                                    setSelectedPeopleCareAssessment(pc);
                                                                    setShowPeopleCareModal(true);
                                                                }}
                                                                className="p-3 bg-white rounded-2xl border border-teal-100 hover:border-teal-300 cursor-pointer flex items-center justify-between text-xs transition shadow-2xs"
                                                            >
                                                                <span className="font-bold text-slate-800 flex items-center gap-1.5">
                                                                    <span>{isOut ? '🌳' : '🌱'}</span> {isOut ? 'Valutazione OUT (Fine)' : 'Valutazione IN (Inizio)'}
                                                                </span>
                                                                <div className="flex items-center gap-2">
                                                                    {pc.sectionB?.averageScore && (
                                                                        <span className="font-black text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-lg text-xs">
                                                                            ★ {pc.sectionB.averageScore}
                                                                        </span>
                                                                    )}
                                                                    <span className="text-[11px] text-slate-400">
                                                                        {new Date(pc.timestamp || pc.date).toLocaleDateString('it-IT')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-teal-800/80 italic p-3 bg-white/60 rounded-xl">Nessun questionario People Care compilato finora per questo cliente.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* SEZIONE CONTRATTI DI COUNSELING MULTIPLI */}
                                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 space-y-4">
                                    <div className="flex flex-wrap justify-between items-center gap-3 pb-3 border-b border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">📑</span>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-base text-gray-800">Contratti di Counseling</h3>
                                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[11px] font-bold">
                                                        {contracts.length} {contracts.length === 1 ? 'contratto' : 'contratti'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500">Gestione dei contratti, accordi e generazione report dedicati</p>
                                            </div>
                                        </div>
                                        {!isReadOnly && (
                                            <button
                                                onClick={handleOpenNewContract}
                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition text-xs flex items-center gap-1.5 shadow-sm active:scale-98"
                                            >
                                                <span>+</span> Nuovo Contratto
                                            </button>
                                        )}
                                    </div>

                                    {contracts.length === 0 ? (
                                        <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 space-y-2">
                                            <span className="text-3xl block">📑</span>
                                            <p className="font-bold text-slate-700 text-sm">Nessun contratto di counseling presente</p>
                                            <p className="text-xs text-slate-400 max-w-md mx-auto">
                                                Crea un contratto per definire gli obiettivi di lavoro con il cliente e generare report dedicati per ciascun percorso.
                                            </p>
                                            {!isReadOnly && (
                                                <button
                                                    onClick={handleOpenNewContract}
                                                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition"
                                                >
                                                    + Crea Primo Contratto
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {contracts.map(contract => {
                                                const contractMeetings = meetings.filter(m => (m.contractId === contract.id || (contract.id === 'contract_legacy' && !m.contractId)));
                                                const isContractActive = contract.status === 'attivo' || !contract.status;
                                                return (
                                                    <div
                                                        key={contract.id}
                                                        className={`p-5 rounded-2xl border-2 transition-all ${
                                                            isContractActive 
                                                                ? 'bg-gradient-to-br from-blue-50/40 via-white to-slate-50/50 border-blue-200 shadow-sm' 
                                                                : 'bg-slate-50/70 border-slate-200'
                                                        }`}
                                                    >
                                                        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                                                            <div>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h4 className="text-base font-black text-slate-900">{contract.title || 'Contratto senza titolo'}</h4>
                                                                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                                                                        isContractActive 
                                                                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                                                                            : 'bg-slate-100 text-slate-600 border-slate-300'
                                                                    }`}>
                                                                        {isContractActive ? '● Attivo' : '○ Concluso'}
                                                                    </span>
                                                                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md text-[10px] font-bold">
                                                                        🛋️ {contractMeetings.length} {contractMeetings.length === 1 ? 'incontro' : 'incontri'}
                                                                    </span>
                                                                </div>
                                                                <div className="text-[11px] text-slate-500 font-medium mt-1 flex items-center gap-2">
                                                                    <span>🗓️ Inizio: {contract.startDate ? new Date(contract.startDate).toLocaleDateString('it-IT') : 'N/D'}</span>
                                                                    {contract.endDate && (
                                                                        <span>• Fine: {new Date(contract.endDate).toLocaleDateString('it-IT')}</span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Action Buttons for Contract */}
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedContractForReport(contract.id);
                                                                        setShowClientReport(true);
                                                                    }}
                                                                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-xl text-xs font-bold transition flex items-center gap-1 shadow-2xs"
                                                                    title="Genera Report Cliente PDF per questo specifico contratto"
                                                                >
                                                                    <span>📄</span> Report PDF
                                                                </button>
                                                                {!isReadOnly && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleOpenEditContract(contract)}
                                                                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition flex items-center gap-1"
                                                                        >
                                                                            <span>✏️</span> Modifica
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleToggleContractStatus(contract)}
                                                                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-medium transition"
                                                                            title={isContractActive ? 'Segna come concluso' : 'Riapri contratto come attivo'}
                                                                        >
                                                                            {isContractActive ? '🏁 Concludi' : '🔄 Riapri'}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDeleteContract(contract.id)}
                                                                            className="p-1.5 text-slate-300 hover:text-red-600 rounded-xl transition"
                                                                            title="Elimina contratto"
                                                                        >
                                                                            🗑️
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {contract.description ? (
                                                            <p className="text-xs text-slate-700 bg-white/80 p-3 rounded-xl border border-slate-100 whitespace-pre-wrap leading-relaxed">
                                                                {contract.description}
                                                            </p>
                                                        ) : (
                                                            <p className="text-xs text-slate-400 italic">Nessun dettaglio o obiettivo specificato per questo contratto.</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Modalità Gestione Dati */}
                                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 space-y-3">
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                        <span>⚙️</span> Modalità Gestione Dati
                                    </p>
                                    <div className="space-y-2">
                                        <label className="flex items-start gap-3 p-3 rounded-2xl border-2 cursor-pointer transition hover:bg-blue-50"
                                            style={{ borderColor: (enrichedClient.mode === 'autonomous' || !enrichedClient.mode) ? '#3b82f6' : '#e5e7eb' }}>
                                            <input
                                                type="radio"
                                                name="mode"
                                                value="autonomous"
                                                checked={enrichedClient.mode === 'autonomous' || !enrichedClient.mode}
                                                onChange={async () => {
                                                    await DataManager.updateClientMode(client.id, 'autonomous', enrichedClient.isActive !== false);
                                                    await refreshData();
                                                }}
                                                className="mt-1"
                                            />
                                            <div className="flex-1">
                                                <div className="font-bold text-xs text-gray-800">🟢 Autonoma</div>
                                                <div className="text-[11px] text-gray-500">Il cliente accede e gestisce le proprie note</div>
                                            </div>
                                        </label>
                                        <label className="flex items-start gap-3 p-3 rounded-2xl border-2 cursor-pointer transition hover:bg-amber-50"
                                            style={{ borderColor: enrichedClient.mode === 'manual' ? '#f59e0b' : '#e5e7eb' }}>
                                            <input
                                                type="radio"
                                                name="mode"
                                                value="manual"
                                                checked={enrichedClient.mode === 'manual'}
                                                onChange={async () => {
                                                    if (confirm('Passare a modalità manuale? Il cliente non potrà accedere direttamente e la scheda Diario verrà nascosta.')) {
                                                        await DataManager.updateClientMode(client.id, 'manual', enrichedClient.isActive !== false);
                                                        if (activeTab === 'works') {
                                                            setActiveTab('meetings');
                                                        }
                                                        await refreshData();
                                                    }
                                                }}
                                                className="mt-1"
                                            />
                                            <div className="flex-1">
                                                <div className="font-bold text-xs text-gray-800">🟡 Manuale</div>
                                                <div className="text-[11px] text-gray-500">Solo il counselor inserisce note ed incontri</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'works' && (
                            <div className="space-y-4">
                                {enrichedClient.mode === 'manual' && (
                                    <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 flex justify-between items-center">
                                        <div>
                                            <h4 className="font-bold text-amber-900">Modalità Manuale Attiva</h4>
                                            <p className="text-sm text-amber-800">
                                                {canEditNotesAndDiaries 
                                                    ? 'Puoi inserire voci di diario o sogni per conto del cliente.' 
                                                    : 'La compilazione e modifica delle voci di diario compete esclusivamente alla counselor assegnata.'}
                                            </p>
                                        </div>
                                        {canEditNotesAndDiaries && (
                                            <button
                                                onClick={() => setShowManualEntryModal(true)}
                                                className="px-4 py-2 bg-amber-600 text-white font-bold rounded-xl shadow-sm hover:bg-amber-700 transition"
                                            >
                                                + Aggiungi Voce
                                            </button>
                                        )}
                                    </div>
                                )}

                                {works.length === 0 ? <div className="p-8 text-center bg-white rounded-3xl border border-dashed border-gray-200 text-gray-400">Nessuna attività recente.</div> : works.map(work => (
                                    <div key={work.id} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50 relative group">
                                        {canEditNotesAndDiaries && (
                                            <button onClick={() => handleDeleteWork(work.id)} className="absolute top-4 right-4 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition">🗑️</button>
                                        )}
                                        <div className="flex gap-3 mb-3">
                                            <span className="text-2xl">
                                                {(() => {
                                                    const isDream = work.type === 'dream' || work.type === 'dream_analysis' ||
                                                        (typeof work.content === 'object' && work.content && (work.content.category === 'Sogni' || (work.content.userTags && work.content.userTags.some(t => t.toLowerCase().includes('sogno')))));

                                                    if (isDream) return '🌙';
                                                    if (work.type === 'diary') return '📔';
                                                    if (work.type === 'journal') return '✍️';
                                                    return '📋';
                                                })()}
                                            </span>
                                            <div>
                                                <h4 className="font-bold capitalize text-gray-800">
                                                    {(() => {
                                                        const isDream = work.type === 'dream' || work.type === 'dream_analysis' ||
                                                            (typeof work.content === 'object' && work.content && (work.content.category === 'Sogni' || (work.content.userTags && work.content.userTags.some(t => t.toLowerCase().includes('sogno')))));

                                                        if (isDream) return 'Sogno';
                                                        if (work.type === 'diary') return 'Diario';
                                                        return work.type;
                                                    })()}
                                                </h4>
                                                <div className="text-xs text-gray-400">{new Date(work.timestamp).toLocaleString()}</div>
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-xl text-gray-700 text-sm whitespace-pre-wrap">
                                            {work.type === 'diary' ? (
                                                <p className="italic">{typeof work.content === 'string' ? work.content : (work.content.text || JSON.stringify(work.content))}</p>
                                            ) : work.type === 'journal' ? (
                                                <p className="italic">"{typeof work.content === 'string' ? work.content : work.content.text}"</p>
                                            ) : (
                                                <p>{typeof work.content === 'string' ? work.content : (work.content.text || JSON.stringify(work.content.answers || work.content))}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'meetings' && (
                            <div className="space-y-6">
                                {isForeignSuperAdmin && !isAccessApproved ? (
                                    /* DEONTOLOGICAL PRIVACY SHIELD FOR SUPERADMIN */
                                    <div className="bg-white rounded-3xl border-2 border-slate-200/80 shadow-md p-6 sm:p-10 text-center max-w-2xl mx-auto my-6 animate-fade-in">
                                        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-3xl bg-slate-100 flex items-center justify-center text-3xl sm:text-4xl shadow-inner mb-5">
                                            {superAdminAccessStatus === 'pending' ? '⏳' : superAdminAccessStatus === 'rejected' ? '🚫' : '🛡️'}
                                        </div>

                                        <div className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3 bg-[#f0f5f8] text-[#24485d] border border-[#d1dde5]">
                                            Riservatezza Deontologica
                                        </div>

                                        <h3 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight mb-2">
                                            Note Incontri Protette da Segreto Professionale
                                        </h3>

                                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-lg mx-auto mb-6 font-medium">
                                            Nel rispetto dell'alleanza di counseling e del codice deontologico, le note di colloquio e i contenuti emersi negli incontri sono riservati al Counselor titolare:
                                            <strong className="block text-slate-800 mt-1 text-sm font-bold">
                                                {(() => {
                                                    const rawEmail = (enrichedClient.counselorEmail || client.counselorEmail || '').toLowerCase().trim();
                                                    if (rawEmail === 'martina@gmail.com') return 'Martina Vaccarone';
                                                    if (rawEmail === 'trebino.martina@gmail.com') return 'Martina Trebino';
                                                    const cObj = counselorsList?.find(c => c.id === (enrichedClient.counselorId || client.counselorId) || c.email === (enrichedClient.counselorEmail || client.counselorEmail));
                                                    if (cObj?.email?.toLowerCase() === 'martina@gmail.com') return 'Martina Vaccarone';
                                                    if (cObj?.email?.toLowerCase() === 'trebino.martina@gmail.com') return 'Martina Trebino';
                                                    return cObj?.name || enrichedClient.counselorName || client.counselorName || enrichedClient.counselorEmail || client.counselorEmail || 'Counselor Assegnato';
                                                })()}
                                            </strong>
                                        </p>

                                        {superAdminAccessStatus === 'pending' && (
                                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-left mb-6 max-w-md mx-auto">
                                                <div className="flex items-center gap-2 text-amber-900 font-bold text-xs mb-1">
                                                    <span className="text-base animate-spin">⌛</span>
                                                    <span>Richiesta in attesa di approvazione</span>
                                                </div>
                                                <p className="text-[11px] text-amber-800 leading-normal">
                                                    Hai inoltrato la richiesta di accesso in data {enrichedClient.superAdminAccessRequestedAt ? new Date(enrichedClient.superAdminAccessRequestedAt).toLocaleString('it-IT') : 'recente'}. Il counselor titolare deve approvarla prima che tu possa consultare gli incontri.
                                                </p>
                                                <button
                                                    onClick={handleCancelAccessRequest}
                                                    disabled={isRequestingAccess}
                                                    className="mt-3 text-xs font-bold text-amber-800 hover:text-amber-950 underline cursor-pointer"
                                                >
                                                    Annulla richiesta
                                                </button>
                                            </div>
                                        )}

                                        {superAdminAccessStatus === 'rejected' && (
                                            <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-left mb-6 max-w-md mx-auto">
                                                <div className="flex items-center gap-2 text-rose-900 font-bold text-xs mb-1">
                                                    <span className="text-base">🚫</span>
                                                    <span>Richiesta non approvata</span>
                                                </div>
                                                <p className="text-[11px] text-rose-800 leading-normal">
                                                    Il Counselor titolare ha preferito non condividere le note degli incontri per questa specifica relazione di counseling. Puoi comunque inviare una nuova richiesta specificando una motivazione.
                                                </p>
                                            </div>
                                        )}

                                        {superAdminAccessStatus === 'none' && (
                                            <p className="text-xs text-slate-500 max-w-md mx-auto mb-6">
                                                Per finalità di supervisione didattica, intervisione tra pari o coordinamento dello studio, puoi inoltrare una richiesta formale di consultazione al Counselor titolare.
                                            </p>
                                        )}

                                        {superAdminAccessStatus !== 'pending' && (
                                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                                <button
                                                    onClick={() => setShowRequestAccessModal(true)}
                                                    className="w-full sm:w-auto px-6 py-3 bg-[#1e3847] hover:bg-[#24485d] text-white rounded-2xl font-bold text-xs sm:text-sm shadow-md transition active:scale-95 flex items-center justify-center gap-2"
                                                >
                                                    <span>📩</span> Richiedi Accesso al Counselor
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <div className="flex flex-wrap gap-2 items-center">
                                                {!showMeetingForm && !isReadOnly && (
                                                    <>
                                                        <Button onClick={() => setShowMeetingForm(true)} className="bg-gray-800 text-white text-sm px-4 py-2 rounded-lg">+ Nuovo Incontro</Button>
                                                        <button
                                                            onClick={() => setShowImportModal(true)}
                                                            className="bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm px-4 py-2 rounded-lg font-medium transition"
                                                        >
                                                            📥 Importa da File
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => setShowNotesTranscriptModal(true)}
                                                    className="bg-purple-100 text-purple-700 hover:bg-purple-200 text-sm px-4 py-2 rounded-lg font-bold transition flex items-center gap-1.5 shadow-sm"
                                                    title="Visualizza, filtra, copia ed esporta tutte le note registrate"
                                                >
                                                    <span>📋</span>
                                                    <span>Trascrizione Note</span>
                                                </button>
                                                <button
                                                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm px-4 py-2 rounded-lg font-bold transition flex items-center gap-2"
                                                >
                                                    {sortOrder === 'asc' ? '⏳ Vecchi → Nuovi' : '⌛ Nuovi → Vecchi'}
                                                </button>
                                            </div>
                                            <button onClick={() => setShowBin(!showBin)} className={`text-sm px-3 py-1 rounded-lg ${showBin ? 'text-red-600 bg-red-50' : 'text-gray-400'}`}>🗑️ Cestino</button>
                                        </div>

                                 {showMeetingForm && (
                                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-blue-100">
                                        <h3 className="font-bold mb-4">{editingMeetingId ? 'Modifica Incontro' : 'Nuovo Incontro'}</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1 font-medium">Data</label>
                                                <input type="date" className="w-full p-2 border rounded-lg" value={meetingForm.date} onChange={e => setMeetingForm({ ...meetingForm, date: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1 font-medium">Tipologia Incontro</label>
                                                <select
                                                    className="w-full p-2 border rounded-lg font-medium"
                                                    value={meetingForm.meetingType}
                                                    onChange={e => {
                                                        const type = e.target.value;
                                                        setMeetingForm({ ...meetingForm, meetingType: type });
                                                        // Auto-show wheel for checkpoint-type meetings
                                                        if (['checkpoint', 'primo_colloquio', 'chiusura_percorso'].includes(type)) {
                                                            setShowCheckpointWheel(true);
                                                        } else {
                                                            setShowCheckpointWheel(false);
                                                        }
                                                    }}
                                                >
                                                    <option value="colloquio">🛋️ Colloquio</option>
                                                    <option value="checkpoint">📊 Checkpoint</option>
                                                    <option value="primo_colloquio">🌱 Primo colloquio</option>
                                                    <option value="chiusura_percorso">🏁 Chiusura percorso</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1 font-medium">Contratto Associato</label>
                                                <select
                                                    className="w-full p-2 border rounded-lg font-medium text-xs text-slate-800"
                                                    value={meetingForm.contractId || ''}
                                                    onChange={e => setMeetingForm({ ...meetingForm, contractId: e.target.value })}
                                                >
                                                    <option value="">Nessun Contratto / Generale</option>
                                                    {contracts.map(c => (
                                                        <option key={c.id} value={c.id}>
                                                            📑 {c.title} {c.status === 'attivo' || !c.status ? '(Attivo)' : '(Concluso)'}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1 font-medium">Snapshot Emotivo</label>
                                                <select className="w-full p-2 border rounded-lg" value={meetingForm.emoji} onChange={e => setMeetingForm({ ...meetingForm, emoji: e.target.value })}>
                                                    <option value="😌">😌 Calmo</option>
                                                    <option value="😊">😊 Sereno</option>
                                                    <option value="😰">😰 Ansioso</option>
                                                    <option value="😠">😠 Arrabbiato</option>
                                                    <option value="😢">😢 Triste</option>
                                                    <option value="🤔">🤔 Pensieroso</option>
                                                    <option value="🤯">🤯 Confuso</option>
                                                    <option value="😐">😐 Neutro</option>
                                                    <option value="😤">😤 Frustrato</option>
                                                    <option value="😴">😴 Stanco</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Observation Notes */}
                                        <div className="mb-4">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <label className="block text-xs text-gray-700 font-bold">📋 Note di Osservazione (riservate)</label>
                                                {!canEditNotesAndDiaries ? (
                                                    <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded font-bold">
                                                        🔒 Solo lettura (modifica riservata alla counselor assegnata)
                                                    </span>
                                                ) : (
                                                    <VoiceDictationButton
                                                        label="Detta nota"
                                                        currentValue={meetingForm.notes}
                                                        onTranscript={(updater) => {
                                                            setMeetingForm(prev => ({
                                                                ...prev,
                                                                notes: typeof updater === 'function' ? updater(prev.notes) : updater
                                                            }));
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            {canEditNotesAndDiaries ? (
                                                <RichTextEditor
                                                    value={meetingForm.notes}
                                                    onChange={(newVal) => setMeetingForm(prev => ({ ...prev, notes: newVal }))}
                                                    placeholder="Osservazioni cliniche, dinamiche, vissuti emersi (i pulsanti formattano direttamente il testo)..."
                                                    minHeight="140px"
                                                />
                                            ) : (
                                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 min-h-[100px] whitespace-pre-wrap">
                                                    {meetingForm.notes || <span className="italic text-slate-400">Nessuna nota registrata.</span>}
                                                </div>
                                            )}
                                        </div>

                                        {/* Counselor Feeling */}
                                        <div className="mb-4">
                                            <div className="flex justify-between items-center mb-1">
                                                <label className="block text-xs text-gray-600 font-medium">🧘 Counselor (come ti sei sentito/a durante la sessione)</label>
                                                {canEditNotesAndDiaries && (
                                                    <VoiceDictationButton
                                                        label="Detta vissuto"
                                                        currentValue={meetingForm.counselorFeeling}
                                                        onTranscript={(updater) => {
                                                            setMeetingForm(prev => ({
                                                                ...prev,
                                                                counselorFeeling: typeof updater === 'function' ? updater(prev.counselorFeeling) : updater
                                                            }));
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            {canEditNotesAndDiaries ? (
                                                <textarea
                                                    className="w-full p-3 border rounded-xl bg-gray-50 focus:bg-white transition-colors"
                                                    value={meetingForm.counselorFeeling}
                                                    onChange={e => setMeetingForm({ ...meetingForm, counselorFeeling: e.target.value })}
                                                    placeholder="Come ti sei sentito/a durante la sessione? Sentito corporeo, risonanze, controtransfert..."
                                                    rows="3"
                                                ></textarea>
                                            ) : (
                                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 min-h-[60px] whitespace-pre-wrap">
                                                    {meetingForm.counselorFeeling || <span className="italic text-slate-400">Nessun vissuto registrato.</span>}
                                                </div>
                                            )}
                                        </div>

                                        {/* Checkpoint: Wheel of Life inline */}
                                        {['checkpoint', 'primo_colloquio', 'chiusura_percorso'].includes(meetingForm.meetingType) && (
                                            <div className="mb-4 bg-indigo-50 p-5 rounded-2xl border border-indigo-100">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xl">🎡</span>
                                                        <div>
                                                            <label className="block text-sm font-black text-indigo-900 uppercase tracking-tighter">Ruota della Vita</label>
                                                            <p className="text-[10px] text-indigo-600 font-medium">
                                                                {meetingForm.meetingType === 'primo_colloquio' ? 'Valutazione iniziale' :
                                                                    meetingForm.meetingType === 'chiusura_percorso' ? 'Valutazione finale' :
                                                                        'Snapshot intermedio'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowCheckpointWheel(!showCheckpointWheel)}
                                                        className={`text-xs px-3 py-1.5 rounded-full font-bold transition ${showCheckpointWheel
                                                            ? 'bg-indigo-600 text-white'
                                                            : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100'}`}
                                                    >
                                                        {showCheckpointWheel ? '✓ Compila Ruota' : '+ Aggiungi Ruota'}
                                                    </button>
                                                </div>

                                                {showCheckpointWheel && (
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in">
                                                        {[
                                                            { key: 'Lavoro', icon: '💼', label: 'Lavoro' },
                                                            { key: 'Famiglia', icon: '👨‍👩‍👧‍👦', label: 'Famiglia' },
                                                            { key: 'Relazioni', icon: '💕', label: 'Relazioni' },
                                                            { key: 'Salute', icon: '🏥', label: 'Salute' },
                                                            { key: 'Emozioni', icon: '🎭', label: 'Emozioni' },
                                                            { key: 'Finanze', icon: '💰', label: 'Finanze' },
                                                            { key: 'Crescita', icon: '📚', label: 'Crescita' },
                                                            { key: 'Ambiente', icon: '🌍', label: 'Ambiente' }
                                                        ].map(area => (
                                                            <div key={area.key} className="bg-white p-3 rounded-xl border border-indigo-100 text-center">
                                                                <div className="text-lg mb-1">{area.icon}</div>
                                                                <div className="text-[10px] font-bold text-slate-500 uppercase mb-2">{area.label}</div>
                                                                <div className="text-xl font-black text-indigo-600 mb-1">{checkpointWheel[area.key]}</div>
                                                                <input
                                                                    type="range"
                                                                    min="1"
                                                                    max="10"
                                                                    value={checkpointWheel[area.key]}
                                                                    onChange={e => setCheckpointWheel({ ...checkpointWheel, [area.key]: parseInt(e.target.value) })}
                                                                    className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Merged Techniques & Tools */}
                                        <div className="mb-6 bg-slate-50 p-5 rounded-3xl border-2 border-slate-100 shadow-sm relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-slate-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <span>🛠️</span> Tecniche Utilizzate
                                            </label>
                                            
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative z-10">
                                                {/* Default Techniques */}
                                                {DEFAULT_TECHNIQUES.map(ex => {
                                                    const isSelected = meetingForm.exercises?.includes(ex.id);
                                                    return (
                                                        <button
                                                            key={ex.id}
                                                            type="button"
                                                            onClick={() => {
                                                                const current = meetingForm.exercises || [];
                                                                const next = isSelected 
                                                                    ? current.filter(id => id !== ex.id)
                                                                    : [...current, ex.id];
                                                                setMeetingForm({ ...meetingForm, exercises: next });
                                                            }}
                                                            className={`flex items-center gap-2 p-3 rounded-xl border-2 font-bold transition-all ${
                                                                isSelected 
                                                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100 scale-[1.02]' 
                                                                    : 'bg-white border-slate-100 text-slate-600 hover:border-indigo-200 hover:bg-indigo-50/30'
                                                            }`}
                                                        >
                                                            <span className="text-xl">{ex.icon}</span>
                                                            <span className="text-[10px] uppercase tracking-tight">{ex.label}</span>
                                                        </button>
                                                    );
                                                })}

                                                {/* Custom Techniques */}
                                                {customTechniques.map((tech, i) => {
                                                    const isSelected = meetingForm.exercises?.includes(tech);
                                                    return (
                                                        <button
                                                            key={`custom-${i}`}
                                                            type="button"
                                                            onClick={() => {
                                                                const current = meetingForm.exercises || [];
                                                                const next = isSelected 
                                                                    ? current.filter(t => t !== tech)
                                                                    : [...current, tech];
                                                                setMeetingForm({ ...meetingForm, exercises: next });
                                                            }}
                                                            className={`flex items-center gap-2 p-3 rounded-xl border-2 font-bold transition-all ${
                                                                isSelected 
                                                                    ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-100 scale-[1.02]' 
                                                                    : 'bg-white border-slate-100 text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/30'
                                                            }`}
                                                        >
                                                            <span className="text-xl">🛠️</span>
                                                            <span className="text-[10px] uppercase tracking-tight truncate">{tech}</span>
                                                        </button>
                                                    );
                                                })}

                                                {/* Add Other Button */}
                                                {!showOtherInput ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowOtherInput(true)}
                                                        className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 font-bold hover:bg-slate-100 transition-all"
                                                    >
                                                        <span className="text-lg">+</span>
                                                        <span className="text-[10px] uppercase">Altro...</span>
                                                    </button>
                                                ) : (
                                                    <div className="col-span-2 flex gap-1 animate-fade-in">
                                                        <input
                                                            type="text"
                                                            value={otherTechnique}
                                                            onChange={(e) => setOtherTechnique(e.target.value)}
                                                            placeholder="Nuova tecnica..."
                                                            className="flex-1 p-3 border-2 border-indigo-200 rounded-xl text-[10px] font-bold outline-none focus:border-indigo-500"
                                                            autoFocus
                                                            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTechnique()}
                                                        />
                                                        <button 
                                                            type="button"
                                                            onClick={handleAddCustomTechnique}
                                                            className="bg-indigo-600 text-white px-3 rounded-xl font-bold text-xs"
                                                        >
                                                            Aggiungi
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => setShowOtherInput(false)}
                                                            className="bg-gray-100 text-gray-400 px-3 rounded-xl font-bold text-xs"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Manual Tags */}
                                        <div className="mb-4 bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-xl border-2 border-green-200">
                                            <label className="block text-sm text-green-900 mb-2 font-bold">
                                                🎯 Tag Sessione (per Mappa Osservata)
                                            </label>
                                            <p className="text-xs text-green-700 mb-2">
                                                Inserisci parole chiave separate da virgola. Questo campo è la FONTE per gli alert AI e la Mappa Osservata.
                                            </p>
                                            <input
                                                type="text"
                                                className="w-full p-3 border-2 border-green-300 rounded-lg font-mono text-sm bg-white"
                                                value={meetingForm.tags}
                                                onChange={e => setMeetingForm({ ...meetingForm, tags: e.target.value })}
                                                placeholder="Es: Contatto, Rabbia, Negazione, Confine"
                                            />
                                        </div>

                                        <div className="flex justify-end gap-2">
                                            <button onClick={resetMeetingForm} className="px-3 py-1 bg-gray-100 rounded text-xs">Annulla</button>
                                            <Button onClick={handleSaveMeeting}>Salva Incontro</Button>
                                        </div>
                                    </div>
                                )}

                                {/* Contract Filter Bar */}
                                {contracts.length > 0 && (
                                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider shrink-0 mr-1">Filtra:</span>
                                        <button
                                            onClick={() => setContractFilterMeetings('all')}
                                            className={`px-3 py-1 rounded-full text-xs font-bold transition whitespace-nowrap ${
                                                contractFilterMeetings === 'all'
                                                    ? 'bg-slate-900 text-white shadow-xs'
                                                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                                            }`}
                                        >
                                            Tutti ({meetings.length})
                                        </button>
                                        {contracts.map(c => {
                                            const count = meetings.filter(m => m.contractId === c.id || (c.id === 'contract_legacy' && !m.contractId)).length;
                                            const isSelected = contractFilterMeetings === c.id;
                                            return (
                                                <button
                                                    key={c.id}
                                                    onClick={() => setContractFilterMeetings(c.id)}
                                                    className={`px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1.5 whitespace-nowrap ${
                                                        isSelected
                                                            ? 'bg-blue-600 text-white shadow-xs'
                                                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    <span>📑</span>
                                                    <span className="truncate max-w-[140px]">{c.title}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                                        {count}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {meetingsLoadError && (
                                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800 text-xs flex items-start gap-3 shadow-sm">
                                        <span className="text-xl">⚠️</span>
                                        <div>
                                            <p className="font-bold">Regole di Sicurezza Firestore in aggiornamento</p>
                                            <p className="mt-0.5 text-amber-700">
                                                Non è stato possibile caricare gli incontri da Firestore ({meetingsLoadError}).
                                                Per applicare l'autorizzazione di lettura per SuperAdmin su Google Cloud, esegui nel terminale:
                                                <code className="block mt-1 font-mono bg-white/70 px-2 py-1 rounded border border-amber-200 text-[11px] font-bold">npx firebase deploy --only firestore:rules</code>
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {sortedMeetings.length === 0 ? (
                                    <div className="bg-white p-8 rounded-3xl text-center border border-gray-100 shadow-sm text-gray-500 py-12">
                                        <div className="text-4xl mb-3">🛋️</div>
                                        <p className="font-bold text-gray-700 text-base">Nessun incontro presente</p>
                                        <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                                            {isReadOnly 
                                                ? "Non risultano incontri salvati per questa cartella o le regole di lettura sul database non sono ancora distribuite." 
                                                : "Non hai ancora registrato incontri per questo cliente o per il contratto selezionato. Clicca su '+ Nuovo Incontro' in alto per iniziare."}
                                        </p>
                                    </div>
                                ) : (
                                    sortedMeetings.map(meeting => (
                                        <div key={meeting.id} className="bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
                                            <div className="flex justify-between items-center flex-wrap gap-2">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold">
                                                        {(() => {
                                                            const d = meeting.date;
                                                            if (!d) return 'Data N/D';
                                                            if (typeof d === 'string' && d.includes('/')) return d;
                                                            try {
                                                                return new Date(d).toLocaleDateString('it-IT');
                                                            } catch (e) {
                                                                return d;
                                                            }
                                                        })()}
                                                    </span>
                                                    {/* Meeting Type Badge */}
                                                    {(() => {
                                                        const type = meeting.observations?.meetingType || meeting.meetingType || 'colloquio';
                                                        const badges = {
                                                            colloquio: { label: 'Colloquio', icon: '🛋️', color: 'bg-gray-100 text-gray-600' },
                                                            checkpoint: { label: 'Checkpoint', icon: '📊', color: 'bg-indigo-100 text-indigo-700' },
                                                            primo_colloquio: { label: 'Primo colloquio', icon: '🌱', color: 'bg-emerald-100 text-emerald-700' },
                                                            chiusura_percorso: { label: 'Chiusura', icon: '🏁', color: 'bg-amber-100 text-amber-700' }
                                                        };
                                                        const badge = badges[type] || badges.colloquio;
                                                        return (
                                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badge.color}`}>
                                                                {badge.icon} {badge.label}
                                                            </span>
                                                        );
                                                    })()}
                                                    {/* Contract Badge */}
                                                    {(() => {
                                                        const mContractId = meeting.contractId || meeting.observations?.contractId;
                                                        const mContract = contracts.find(c => c.id === mContractId);
                                                        if (!mContract) return null;
                                                        return (
                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                                                📑 {mContract.title}
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                                <span>{meeting.counselorEmoji}</span>
                                            </div>
                                            <div className="mt-2.5 text-xs sm:text-sm text-slate-800 bg-slate-50/70 p-3.5 rounded-xl border border-slate-100/80">
                                                <FormattedNoteText
                                                    text={meeting.counselorNotes || meeting.notes}
                                                    fallback="Nessuna osservazione registrata per questa sessione."
                                                />
                                            </div>
                                            
                                            {( (meeting.observations?.techniques && meeting.observations.techniques.trim()) || (meeting.observations?.exercises && meeting.observations.exercises.length > 0) ) && (
                                                <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                                                    <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest mr-1">Tecniche:</span>
                                                    {/* Show text-based techniques if any (legacy) */}
                                                    {meeting.observations?.techniques?.split(',').filter(t => t.trim()).map((t, i) => (
                                                        <span key={`tech-${i}`} className="text-[10px] bg-blue-100/50 text-blue-700 px-2 py-0.5 rounded-md font-bold border border-blue-100">
                                                            {t.trim()}
                                                        </span>
                                                    ))}
                                                    {/* Show selected boxes (new & old) */}
                                                    {meeting.observations?.exercises?.map((exId, i) => {
                                                        const labels = {
                                                            respirazione: 'Respirazione', carte_creative: 'Carte Creative', frequenze: 'Frequenze',
                                                            sogno: 'Lavoro Onirico', sedia_vuota: 'Sedia Vuota', baseline: 'Test Baseline',
                                                            body_scan: 'Grounding', scrittura: 'Scrittura'
                                                        };
                                                        return (
                                                            <span key={`ex-${i}`} className="text-[10px] bg-indigo-100/50 text-indigo-700 px-2 py-0.5 rounded-md font-bold border border-indigo-100">
                                                                {labels[exId] || exId}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            
                                            {(meeting.counselorTags || meeting.tags) && (
                                                <div className="mt-2 flex flex-wrap gap-1 items-center">
                                                    <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mr-1">Temi:</span>
                                                    {(meeting.counselorTags || meeting.tags).split(',').filter(t => t.trim()).map((t, i) => (
                                                        <span key={i} className="text-[10px] text-emerald-600 font-bold">
                                                            #{t.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Legacy: show old Gestalt observations if they exist */}
                                            {meeting.observations && (meeting.observations.apertura || meeting.observations.flusso || meeting.observations.chiusura) && (
                                                <div className="mt-3 bg-slate-50 p-3 rounded-xl text-[10px] text-slate-500 border border-slate-100 italic">
                                                    <span className="font-bold not-italic text-slate-400 uppercase tracking-wider">Note precedenti: </span>
                                                    {[meeting.observations.apertura, meeting.observations.flusso, meeting.observations.chiusura].filter(Boolean).join(' • ')}
                                                </div>
                                            )}

                                            {/* Counselor Feeling */}
                                            {meeting.counselorFeeling && (
                                                <div className="mt-3 text-sm text-gray-700 bg-indigo-50/40 p-3 rounded-xl border border-indigo-100/70">
                                                    <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest block mb-1">Counselor (vissuto):</span>
                                                    <div className="text-xs text-indigo-950 italic">
                                                        <FormattedNoteText text={meeting.counselorFeeling} />
                                                    </div>
                                                </div>
                                            )}

                                            {!isReadOnly && (!meeting.deletedAt ? (
                                                <div className="mt-2 flex gap-2">
                                                    <button onClick={() => handleEditMeeting(meeting)} className="text-xs bg-blue-50 text-blue-600 px-2 rounded">
                                                        {canEditNotesAndDiaries ? 'Modifica' : 'Dettagli / Tipologia'}
                                                    </button>
                                                    {canEditNotesAndDiaries && (
                                                        <button onClick={() => handleDeleteMeeting(meeting.id)} className="text-xs bg-red-50 text-red-600 px-2 rounded">Cestina</button>
                                                    )}
                                                </div>
                                            ) : (
                                                canEditNotesAndDiaries && (
                                                    <div className="mt-2 flex gap-2">
                                                        <button onClick={() => handleRestoreMeeting(meeting.id)} className="text-xs bg-green-50 text-green-600 px-2 rounded font-bold">Ripristina</button>
                                                        <button
                                                            onClick={async () => {
                                                                if (confirm("Eliminare DEFINITIVAMENTE questo incontro? L'azione non è reversibile.")) {
                                                                    await DataManager.deleteMeeting(meeting.id);
                                                                    await refreshData();
                                                                }
                                                            }}
                                                            className="text-xs bg-gray-100 text-gray-500 hover:bg-red-600 hover:text-white px-2 rounded transition"
                                                        >
                                                            Elimina Definitivamente
                                                        </button>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    ))
                                )}
                                    </>
                                )}
                            </div>
                        )}

                        {(activeTab === 'baseline' || activeTab === 'at_analysis') && (
                            <div className="space-y-12 animate-fade-in">
                                {showAtExperiment ? (
                                    <TransactionalAnalysisExperiment
                                        preselectedClientId={client.id}
                                        clients={[enrichedClient]}
                                        onBack={() => { setShowAtExperiment(false); refreshData(); }}
                                    />
                                ) : showDtExperiment ? (
                                    <DramaTriangleExperiment
                                        client={enrichedClient}
                                        onBack={() => setShowDtExperiment(false)}
                                        onSave={() => { setShowDtExperiment(false); refreshData(); }}
                                    />
                                ) : (
                                    <>
                                        {/* 1. Baseline Evolution Report (Questionari & Evoluzione) */}
                                        <div>
                                            <BaselineEvolutionReport client={enrichedClient} works={works} />
                                        </div>

                                        {/* 2. Analisi Transazionale (A.T.) & Triangolo Drammatico */}
                                        <div className="space-y-6 pt-4">
                                            <div className="flex items-center gap-4">
                                                <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                                    <span>✨</span> Analisi Transazionale (A.T.) & Stati dell'Io
                                                </h3>
                                                <div className="h-px flex-1 bg-slate-200"></div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* AT Header Card */}
                                                <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-indigo-50 flex flex-col justify-between">
                                                    <div>
                                                        <h3 className="text-xl font-black text-indigo-950">Analisi Transazionale (A.T.)</h3>
                                                        <p className="text-xs text-indigo-500 font-bold uppercase tracking-widest mt-1">Evoluzione degli Stati dell'Io</p>
                                                    </div>
                                                    <div className="mt-6 flex gap-3 flex-wrap">
                                                        <Button onClick={() => setShowAtExperiment(true)} className="bg-indigo-600 text-[11px] px-4 py-2">
                                                            ✨ Nuovo Egogramma
                                                        </Button>
                                                        <button
                                                            onClick={() => setShowDtExperiment(true)}
                                                            className="bg-rose-50 text-rose-600 font-bold text-[11px] px-4 py-2 rounded-xl hover:bg-rose-100 transition"
                                                        >
                                                            ⚔️ Triangolo Drammatico
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Wheel of Life Contextualized */}
                                                <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-emerald-50">
                                                    <h4 className="text-xs font-black text-emerald-800 uppercase tracking-widest mb-4">Ruota della Vita (Contestuale)</h4>
                                                    <div className="h-40">
                                                        <WheelOfLifeEvolution client={enrichedClient} isCompact={true} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* AT Analysis History */}
                                            <div className="space-y-4">
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2 flex items-center gap-3">
                                                    <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                                                    Storico Egogrammi
                                                </h4>
                                                {atExperiments.length === 0 ? (
                                                    <div className="p-8 text-center bg-white rounded-[2rem] border-2 border-dashed border-slate-100">
                                                        <span className="text-3xl mb-2 block">✨</span>
                                                        <p className="text-slate-400 font-bold text-xs italic">Nessuna analisi AT ancora registrata.</p>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        {atExperiments.map((exp, idx) => (
                                                            <div key={exp.id} className="bg-white p-6 rounded-[2rem] shadow-md border border-slate-100 group relative">
                                                                <div className="flex justify-between items-start mb-4">
                                                                    <h4 className="text-sm font-black text-slate-800 italic">
                                                                        {new Date(exp.timestamp).toLocaleDateString('it-IT')}
                                                                    </h4>
                                                                    <button
                                                                        onClick={async () => {
                                                                            if (confirm("Eliminare questa analisi AT?")) {
                                                                                await DataManager.deleteAssessment(exp.id);
                                                                                refreshData();
                                                                            }
                                                                        }}
                                                                        className="text-slate-200 hover:text-red-500 transition-colors"
                                                                    >🗑️</button>
                                                                </div>

                                                                <div className="flex items-end justify-between h-24 gap-2 px-2 bg-slate-50/50 rounded-xl p-3">
                                                                    {(exp.results || []).map(res => (
                                                                        <div key={res.id} className="flex-1 flex flex-col items-center">
                                                                            <div className="w-full bg-slate-200 rounded-full h-full relative overflow-hidden flex items-end">
                                                                                <div
                                                                                    className={`w-full transition-all duration-1000 ${res.id === 'parent_critical' ? 'bg-rose-400' :
                                                                                        res.id === 'parent_nurturing' ? 'bg-emerald-400' :
                                                                                            res.id === 'adult' ? 'bg-slate-400' :
                                                                                                res.id === 'child_free' ? 'bg-amber-400' : 'bg-indigo-400'
                                                                                        }`}
                                                                                    style={{ height: `${res.percentage}%` }}
                                                                                />
                                                                            </div>
                                                                            <span className="mt-1 text-[7px] font-black text-slate-500 uppercase">
                                                                                {res.id === 'parent_critical' ? 'GN' : res.id === 'parent_nurturing' ? 'GA' : res.id === 'adult' ? 'A' : res.id === 'child_free' ? 'BL' : 'BA'}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Drama Triangle History */}
                                            <div className="space-y-4 pt-2">
                                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2 flex items-center gap-3">
                                                    <span className="w-2 h-2 bg-rose-400 rounded-full"></span>
                                                    Storico Triangolo Drammatico
                                                </h4>
                                                {dtExperiments.length === 0 ? (
                                                    <p className="text-xs text-slate-400 italic px-2">Nessuna analisi del triangolo registrata.</p>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        {dtExperiments.map((exp) => (
                                                            <div key={exp.id} className="bg-white p-6 rounded-[2rem] border border-rose-100 shadow-sm relative group">
                                                                <div className="flex justify-between items-center mb-4">
                                                                    <span className="text-[10px] font-bold text-slate-400">{new Date(exp.timestamp).toLocaleDateString()}</span>
                                                                    <button
                                                                        onClick={async () => {
                                                                            if (confirm("Eliminare questa analisi?")) {
                                                                                await DataManager.deleteAssessment(exp.id);
                                                                                refreshData();
                                                                            }
                                                                        }}
                                                                        className="text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100"
                                                                    >🗑️</button>
                                                                </div>
                                                                <div className="space-y-3">
                                                                    {(exp.results || []).map(r => (
                                                                        <div key={r.id}>
                                                                            <div className="flex justify-between text-[9px] font-black uppercase text-slate-600 mb-1">
                                                                                <span>{r.name}</span>
                                                                                <span>{r.percentage}%</span>
                                                                            </div>
                                                                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                                                <div
                                                                                    className={`h-full ${r.id === 'persecutor' ? 'bg-rose-500' : r.id === 'rescuer' ? 'bg-indigo-500' : 'bg-amber-500'}`}
                                                                                    style={{ width: `${r.percentage}%` }}
                                                                                ></div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* 3. Sperimenti Somatici (Bioenergetica) */}
                                        <div className="space-y-4 pt-4">
                                            <div className="flex items-center gap-4">
                                                <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                                    <span>🌿</span> Sperimenti Somatici (Bioenergetica)
                                                </h3>
                                                <div className="h-px flex-1 bg-slate-200"></div>
                                            </div>

                                            {somaticExperiments.length === 0 ? (
                                                <p className="text-xs text-slate-400 italic text-center py-6 bg-white rounded-3xl border border-slate-100">
                                                    Nessun esperimento somatico registrato.
                                                </p>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {somaticExperiments.map((exp, i) => (
                                                        <div key={exp.id} className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm relative group">
                                                            <div className="flex justify-between items-start mb-4">
                                                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-tighter">
                                                                    {new Date(exp.timestamp).toLocaleDateString()}
                                                                </p>
                                                                <button
                                                                    onClick={async () => {
                                                                        if (confirm("Eliminare questo esperimento somatico?")) {
                                                                            await DataManager.deleteAssessment(exp.id);
                                                                            refreshData();
                                                                        }
                                                                    }}
                                                                    className="text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-colors"
                                                                >🗑️</button>
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                {(exp.results || []).slice(0, 3).map(res => (
                                                                    <div key={res.id}>
                                                                        <div className="flex justify-between text-[10px] font-bold text-slate-600 mb-0.5">
                                                                            <span>{res.name}</span>
                                                                            <span>{res.percentage}%</span>
                                                                        </div>
                                                                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                                                            <div className="bg-emerald-500 h-full" style={{ width: `${res.percentage}%` }}></div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {activeTab === 'tools' && <CreativeToolsView client={enrichedClient} />}
                        {activeTab === 'supervision' && <SupervisionView
                            client={enrichedClient}
                            meetings={meetings}
                            works={works}
                            onClientUpdate={refreshData}
                            counselor={counselor}
                            onOpenTranscript={() => setShowNotesTranscriptModal(true)}
                        />}
                    </div>
                </div>
            </main>

            {/* Meeting Import Modal */}
            {showImportModal && (
                <MeetingImportModal
                    clientId={client.id}
                    onClose={() => setShowImportModal(false)}
                    onImportComplete={(results) => {
                        setShowImportModal(false);
                        refreshData();
                        alert(`✅ Importati ${results.success.length} colloqui!\n${results.duplicates.length > 0 ? `⚠️ ${results.duplicates.length} duplicati saltati\n` : ''}${results.failed.length > 0 ? `❌ ${results.failed.length} errori` : ''}`);
                    }}
                />
            )}

            {/* Manual Entry Modal */}
            {showManualEntryModal && (
                <ManualEntryModal
                    clientId={client.id}
                    onClose={() => setShowManualEntryModal(false)}
                    onSave={refreshData}
                />
            )}

            {/* Client Report Modal */}
            {showClientReport && (
                <CustomerReportTemplate
                    client={enrichedClient}
                    initialContractId={selectedContractForReport}
                    onClose={() => {
                        setShowClientReport(false);
                        setSelectedContractForReport(null);
                    }}
                />
            )}

            {/* Multi-Contract Modal */}
            {showContractModal && editingContract && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 space-y-4">
                        <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <span className="text-2xl">📑</span>
                                <div>
                                    <h3 className="text-base font-bold text-gray-800">
                                        {editingContract.id ? 'Modifica Contratto di Counseling' : 'Nuovo Contratto di Counseling'}
                                    </h3>
                                    <p className="text-xs text-slate-500">Definisci titolo, accordi, date e stato del percorso</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowContractModal(false);
                                    setEditingContract(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 font-bold"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Titolo del Contratto *</label>
                                <input
                                    type="text"
                                    className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none text-sm font-semibold"
                                    value={editingContract.title}
                                    onChange={e => setEditingContract({ ...editingContract, title: e.target.value })}
                                    placeholder="Es. Percorso Autostima e Relazioni 2026"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Data Inizio</label>
                                    <input
                                        type="date"
                                        className="w-full p-2.5 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none text-xs"
                                        value={editingContract.startDate}
                                        onChange={e => setEditingContract({ ...editingContract, startDate: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Data Fine (Opzionale)</label>
                                    <input
                                        type="date"
                                        className="w-full p-2.5 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none text-xs"
                                        value={editingContract.endDate}
                                        onChange={e => setEditingContract({ ...editingContract, endDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">Stato Contratto</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <label className={`p-2.5 rounded-xl border-2 flex items-center justify-center gap-2 cursor-pointer font-bold text-xs transition ${
                                        editingContract.status === 'attivo' || !editingContract.status
                                            ? 'bg-emerald-50 border-emerald-500 text-emerald-900 shadow-2xs' 
                                            : 'bg-slate-50 border-slate-200 text-slate-600'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="contractStatusRadio"
                                            checked={editingContract.status === 'attivo' || !editingContract.status}
                                            onChange={() => setEditingContract({ ...editingContract, status: 'attivo' })}
                                            className="hidden"
                                        />
                                        <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                                        <span>● Attivo</span>
                                    </label>
                                    <label className={`p-2.5 rounded-xl border-2 flex items-center justify-center gap-2 cursor-pointer font-bold text-xs transition ${
                                        editingContract.status === 'concluso' 
                                            ? 'bg-slate-100 border-slate-600 text-slate-900 shadow-2xs' 
                                            : 'bg-slate-50 border-slate-200 text-slate-600'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="contractStatusRadio"
                                            checked={editingContract.status === 'concluso'}
                                            onChange={() => setEditingContract({ ...editingContract, status: 'concluso' })}
                                            className="hidden"
                                        />
                                        <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                                        <span>○ Concluso</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Obiettivi, Domanda di Counseling & Accordi</label>
                                <textarea
                                    rows="4"
                                    className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none text-xs text-slate-800"
                                    value={editingContract.description}
                                    onChange={e => setEditingContract({ ...editingContract, description: e.target.value })}
                                    placeholder="Descrivi la domanda di counseling, il focus concordato e gli obiettivi attesi..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                                onClick={() => {
                                    setShowContractModal(false);
                                    setEditingContract(null);
                                }}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={() => handleSaveContractModal(editingContract)}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            >
                                Salva Contratto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Counselor Notes Transcript Modal */}
            {showNotesTranscriptModal && (
                <CounselorNotesTranscriptModal
                    client={enrichedClient}
                    meetings={meetings}
                    onClose={() => setShowNotesTranscriptModal(false)}
                />
            )}

            {/* Request SuperAdmin Access Modal */}
            {showRequestAccessModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                                <span className="text-2xl">📩</span>
                                <h3 className="text-lg font-bold text-gray-800">Richiesta Accesso Incontri</h3>
                            </div>
                            <button onClick={() => setShowRequestAccessModal(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
                        </div>
                        
                        <p className="text-xs text-slate-600 mb-4 leading-relaxed">
                            Stai inviando una richiesta di autorizzazione al Counselor titolare (<strong>{(() => {
                                const rawEmail = (enrichedClient.counselorEmail || client.counselorEmail || '').toLowerCase().trim();
                                if (rawEmail === 'martina@gmail.com') return 'Martina Vaccarone';
                                if (rawEmail === 'trebino.martina@gmail.com') return 'Martina Trebino';
                                const cObj = counselorsList?.find(c => c.id === (enrichedClient.counselorId || client.counselorId) || c.email === (enrichedClient.counselorEmail || client.counselorEmail));
                                if (cObj?.email?.toLowerCase() === 'martina@gmail.com') return 'Martina Vaccarone';
                                if (cObj?.email?.toLowerCase() === 'trebino.martina@gmail.com') return 'Martina Trebino';
                                return cObj?.name || enrichedClient.counselorName || client.counselorName || enrichedClient.counselorEmail || client.counselorEmail || 'Counselor Assegnato';
                            })()}</strong>) per visionare gli incontri di <strong>{enrichedClient.name}</strong> a fini di supervisione deontologica e coordinamento didattico.
                        </p>

                        <div className="mb-4">
                            <label className="block text-xs font-bold text-gray-700 mb-1.5">
                                Motivazione / Nota per il Counselor (opzionale)
                            </label>
                            <textarea
                                value={requestAccessNote}
                                onChange={(e) => setRequestAccessNote(e.target.value)}
                                placeholder="Es. Supervisione didattica per passaggio di consegne o verifica obiettivo di percorso..."
                                rows="3"
                                className="w-full p-3 border border-slate-200 rounded-2xl text-xs outline-none focus:border-[#1e3847] focus:ring-1 focus:ring-[#1e3847]"
                            />
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowRequestAccessModal(false)}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleRequestSuperAdminAccess}
                                disabled={isRequestingAccess}
                                className="px-5 py-2 bg-[#1e3847] hover:bg-[#24485d] text-white rounded-xl text-xs font-bold transition shadow-sm disabled:opacity-50"
                            >
                                {isRequestingAccess ? 'Invio in corso...' : 'Invia Richiesta'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* People Care Modal (IN / OUT) */}
            <PeopleCareModal
                isOpen={showPeopleCareModal}
                onClose={() => {
                    setShowPeopleCareModal(false);
                    setSelectedPeopleCareAssessment(null);
                }}
                client={enrichedClient}
                counselor={counselor}
                existingAssessment={selectedPeopleCareAssessment}
                defaultPhase={peopleCareDefaultPhase}
                onSaved={refreshData}
            />

        </div>
    );
};

export default ClientDetailView;
